import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { ChevronDownIcon } from "../components/Icons";
import { useAppFilters } from "../hooks/UseAppFilter";
import {
	COUNTRY_CODES_MAP,
	getCountryInfo,
	groupCountriesByRegion,
	sortRegions,
} from "./CountryCodeUtils";

// Language code to name mapping (keeping original mapping as fallback)
const LANGUAGE_CODES = {
	AA: "Afar",
	AB: "Abkhazian",
	AE: "Avestan",
	AF: "Afrikaans",
	AK: "Akan",
	AM: "Amharic",
	AN: "Aragonese",
	AR: "Arabic",
	AS: "Assamese",
	AV: "Avaric",
	AY: "Aymara",
	AZ: "Azerbaijani",
	BA: "Bashkir",
	BE: "Belarusian",
	BG: "Bulgarian",
	BM: "Bambara",
	BN: "Bengali",
	BO: "Tibetan",
	BR: "Breton",
	BS: "Bosnian",
	CA: "Catalan",
	CE: "Chechen",
	CH: "Chamorro",
	CO: "Corsican",
	CR: "Cree",
	CS: "Czech",
	CU: "Church Slavic",
	CV: "Chuvash",
	CY: "Welsh",
	DA: "Danish",
	DE: "German",
	DV: "Divehi",
	DZ: "Dzongkha",
	EE: "Ewe",
	EL: "Greek",
	EN: "English",
	EO: "Esperanto",
	ES: "Spanish",
	ET: "Estonian",
	EU: "Basque",
	FA: "Persian",
	FF: "Fulah",
	FI: "Finnish",
	FJ: "Fijian",
	FO: "Faroese",
	FR: "French",
	FY: "Western Frisian",
	GA: "Irish",
	GD: "Scottish Gaelic",
	GL: "Galician",
	GN: "Guarani",
	GU: "Gujarati",
	GV: "Manx",
	HA: "Hausa",
	HE: "Hebrew",
	HI: "Hindi",
	HO: "Hiri Motu",
	HR: "Croatian",
	HT: "Haitian",
	HU: "Hungarian",
	HY: "Armenian",
	HZ: "Herero",
	IA: "Interlingua",
	ID: "Indonesian",
	IE: "Interlingue",
	IG: "Igbo",
	II: "Sichuan Yi",
	IK: "Inupiaq",
	IO: "Ido",
	IS: "Icelandic",
	IT: "Italian",
	IU: "Inuktitut",
	JA: "Japanese",
	JV: "Javanese",
	KA: "Georgian",
	KG: "Kongo",
	KI: "Kikuyu",
	KJ: "Kuanyama",
	KK: "Kazakh",
	KL: "Kalaallisut",
	KM: "Central Khmer",
	KN: "Kannada",
	KO: "Korean",
	KR: "Kanuri",
	KS: "Kashmiri",
	KU: "Kurdish",
	KV: "Komi",
	KW: "Cornish",
	KY: "Kirghiz",
	LA: "Latin",
	LB: "Luxembourgish",
	LG: "Ganda",
	LI: "Limburgan",
	LN: "Lingala",
	LO: "Lao",
	LT: "Lithuanian",
	LU: "Luba-Katanga",
	LV: "Latvian",
	MG: "Malagasy",
	MH: "Marshallese",
	MI: "Maori",
	MK: "Macedonian",
	ML: "Malayalam",
	MN: "Mongolian",
	MR: "Marathi",
	MS: "Malay",
	MT: "Maltese",
	MY: "Burmese",
	NA: "Nauru",
	NB: "Norwegian Bokmål",
	ND: "North Ndebele",
	NE: "Nepali",
	NG: "Ndonga",
	NL: "Dutch",
	NN: "Norwegian Nynorsk",
	NO: "Norwegian",
	NR: "South Ndebele",
	NV: "Navajo",
	NY: "Chichewa",
	OC: "Occitan",
	OJ: "Ojibwa",
	OM: "Oromo",
	OR: "Oriya",
	OS: "Ossetian",
	PA: "Panjabi",
	PI: "Pali",
	PL: "Polish",
	PS: "Pushto",
	PT: "Portuguese",
	QU: "Quechua",
	RM: "Romansh",
	RN: "Rundi",
	RO: "Romanian",
	RU: "Russian",
	RW: "Kinyarwanda",
	SA: "Sanskrit",
	SC: "Sardinian",
	SD: "Sindhi",
	SE: "Northern Sami",
	SG: "Sango",
	SH: "Serbo-Croatian",
	SI: "Sinhala",
	SK: "Slovak",
	SL: "Slovenian",
	SM: "Samoan",
	SN: "Shona",
	SO: "Somali",
	SQ: "Albanian",
	SR: "Serbian",
	SS: "Swati",
	ST: "Southern Sotho",
	SU: "Sundanese",
	SV: "Swedish",
	SW: "Swahili",
	TA: "Tamil",
	TE: "Telugu",
	TG: "Tajik",
	TH: "Thai",
	TI: "Tigrinya",
	TK: "Turkmen",
	TL: "Tagalog",
	TN: "Tswana",
	TO: "Tonga",
	TR: "Turkish",
	TS: "Tsonga",
	TT: "Tatar",
	TW: "Twi",
	TY: "Tahitian",
	UG: "Uighur",
	UK: "Ukrainian",
	UR: "Urdu",
	UZ: "Uzbek",
	VE: "Venda",
	VI: "Vietnamese",
	VO: "Volapük",
	WA: "Walloon",
	WO: "Wolof",
	XH: "Xhosa",
	YI: "Yiddish",
	YO: "Yoruba",
	ZA: "Zhuang",
	ZH: "Chinese",
	ZU: "Zulu",
	"ZH-CN": "Chinese (Simplified)",
	"ZH-TW": "Chinese (Traditional)",
	"EN-US": "English (US)",
	"EN-GB": "English (UK)",
	"ES-ES": "Spanish (Spain)",
	"ES-MX": "Spanish (Mexico)",
	"FR-FR": "French (France)",
	"FR-CA": "French (Canada)",
	"PT-BR": "Portuguese (Brazil)",
	"PT-PT": "Portuguese (Portugal)",
	"DE-DE": "German (Germany)",
	"IT-IT": "Italian (Italy)",
	"JA-JP": "Japanese (Japan)",
	"KO-KR": "Korean (Korea)",
	"RU-RU": "Russian (Russia)",
	"AR-SA": "Arabic (Saudi Arabia)",
	"HI-IN": "Hindi (India)",
	"BN-BD": "Bengali (Bangladesh)",
	"UR-PK": "Urdu (Pakistan)",
	"FA-IR": "Persian (Iran)",
	"TR-TR": "Turkish (Turkey)",
	"NL-NL": "Dutch (Netherlands)",
	"SV-SE": "Swedish (Sweden)",
	"DA-DK": "Danish (Denmark)",
	"NO-NO": "Norwegian (Norway)",
	"FI-FI": "Finnish (Finland)",
	"PL-PL": "Polish (Poland)",
	"CS-CZ": "Czech (Czech Republic)",
	"SK-SK": "Slovak (Slovakia)",
	"HU-HU": "Hungarian (Hungary)",
	"RO-RO": "Romanian (Romania)",
	"BG-BG": "Bulgarian (Bulgaria)",
	"HR-HR": "Croatian (Croatia)",
	"SR-RS": "Serbian (Serbia)",
	"SL-SI": "Slovenian (Slovenia)",
	"LT-LT": "Lithuanian (Lithuania)",
	"LV-LV": "Latvian (Latvia)",
	"ET-EE": "Estonian (Estonia)",
	"MT-MT": "Maltese (Malta)",
	"EL-GR": "Greek (Greece)",
	"IS-IS": "Icelandic (Iceland)",
	"MK-MK": "Macedonian (Macedonia)",
	"SQ-AL": "Albanian (Albania)",
	"EU-ES": "Basque (Spain)",
	"CA-ES": "Catalan (Spain)",
	"GL-ES": "Galician (Spain)",
	"BE-BY": "Belarusian (Belarus)",
	"UK-UA": "Ukrainian (Ukraine)",
	"KK-KZ": "Kazakh (Kazakhstan)",
	"KY-KG": "Kirghiz (Kyrgyzstan)",
	"UZ-UZ": "Uzbek (Uzbekistan)",
	"TG-TJ": "Tajik (Tajikistan)",
	"TK-TM": "Turkmen (Turkmenistan)",
	"MN-MN": "Mongolian (Mongolia)",
	"HY-AM": "Armenian (Armenia)",
	"KA-GE": "Georgian (Georgia)",
	"AZ-AZ": "Azerbaijani (Azerbaijan)",
	"TH-TH": "Thai (Thailand)",
	"VI-VN": "Vietnamese (Vietnam)",
	"KM-KH": "Central Khmer (Cambodia)",
	"LO-LA": "Lao (Laos)",
	"MY-MM": "Burmese (Myanmar)",
	"ID-ID": "Indonesian (Indonesia)",
	"MS-MY": "Malay (Malaysia)",
	"TL-PH": "Tagalog (Philippines)",
	"SW-KE": "Swahili (Kenya)",
	"AM-ET": "Amharic (Ethiopia)",
	"SO-SO": "Somali (Somalia)",
	"HA-NG": "Hausa (Nigeria)",
	"YO-NG": "Yoruba (Nigeria)",
	"IG-NG": "Igbo (Nigeria)",
	"ZU-ZA": "Zulu (South Africa)",
	"XH-ZA": "Xhosa (South Africa)",
	"AF-ZA": "Afrikaans (South Africa)",
	"HE-IL": "Hebrew (Israel)",
	"PA-IN": "Panjabi (India)",
	"GU-IN": "Gujarati (India)",
	"MR-IN": "Marathi (India)",
	"KN-IN": "Kannada (India)",
	"ML-IN": "Malayalam (India)",
	"TA-IN": "Tamil (India)",
	"TE-IN": "Telugu (India)",
	"OR-IN": "Oriya (India)",
	"AS-IN": "Assamese (India)",
	"NE-NP": "Nepali (Nepal)",
	"DZ-BT": "Dzongkha (Bhutan)",
	"BO-CN": "Tibetan (China)",
	"MI-NZ": "Maori (New Zealand)",
	"TO-TO": "Tonga (Tonga)",
	"FJ-FJ": "Fijian (Fiji)",
	"SM-WS": "Samoan (Samoa)",
	"FO-FO": "Faroese (Faroe Islands)",
	"KL-GL": "Kalaallisut (Greenland)",
	"IU-CA": "Inuktitut (Canada)",
	"IK-US": "Inupiaq (USA)",
	"NV-US": "Navajo (USA)",
	"CR-CA": "Cree (Canada)",
	"OJ-CA": "Ojibwa (Canada)",
	"GV-IM": "Manx (Isle of Man)",
	"GD-GB": "Scottish Gaelic (UK)",
	"GA-IE": "Irish (Ireland)",
	"BR-FR": "Breton (France)",
	"CO-FR": "Corsican (France)",
	"OC-FR": "Occitan (France)",
	"WA-BE": "Walloon (Belgium)",
	"FY-NL": "Western Frisian (Netherlands)",
	"LI-NL": "Limburgan (Netherlands)",
	"LB-LU": "Luxembourgish (Luxembourg)",
	"RM-CH": "Romansh (Switzerland)",
	"SC-IT": "Sardinian (Italy)",
	"SI-LK": "Sinhala (Sri Lanka)",
	"DV-MV": "Divehi (Maldives)",
	"PS-AF": "Pushto (Afghanistan)",
	"KU-TR": "Kurdish (Turkey)",
	"YI-IL": "Yiddish (Israel)",
	"LA-VA": "Latin (Vatican)",
};

const PublisherSearchInput = React.memo(({ value, onChange }) => {
	const [localValue, setLocalValue] = useState(value || "");

	useEffect(() => {
		setLocalValue(value || "");
	}, [value]);

	const handleChange = useCallback(
		(e) => {
			const newValue = e.target.value;
			setLocalValue(newValue);
			onChange(newValue);
		},
		[onChange],
	);

	return (
		<div className="search-container">
			<input
				type="text"
				placeholder="Search publishers..."
				value={localValue}
				onChange={handleChange}
				className="search-input"
				style={{ fontSize: "11px" }}
				autoComplete="off"
			/>
			{localValue && (
				<button
					onClick={() => {
						setLocalValue("");
						onChange("");
					}}
					className="clear-search-btn"
					type="button"
				>
					×
				</button>
			)}
		</div>
	);
});

const DraggableSlider = React.memo(
	({
		label,
		value,
		min = 0,
		max = 100,
		step = 1,
		unit = "",
		onChange,
		formatValue,
	}) => {
		const [isDragging, setIsDragging] = useState(false);
		const [displayValue, setDisplayValue] = useState(value);
		const [animationId, setAnimationId] = useState(null);
		const sliderRef = useRef(null);
		const dragStartValueRef = useRef(value);

		const { filters, onUpdateFilter } = useAppFilters();

		// Update display value when prop changes (but only if not dragging)
		useEffect(() => {
			if (!isDragging) {
				setDisplayValue(value);
			}
		}, [value, isDragging]);

		const calculateValue = useCallback(
			(clientX) => {
				if (!sliderRef.current) return value;

				const rect = sliderRef.current.getBoundingClientRect();
				const percent = Math.max(
					0,
					Math.min(1, (clientX - rect.left) / rect.width),
				);
				const newValue = min + percent * (max - min);
				const steppedValue = Math.round(newValue / step) * step;
				return Math.max(min, Math.min(max, steppedValue));
			},
			[min, max, step, value],
		);

		// Smooth visual update using requestAnimationFrame
		const updateVisualValue = useCallback(
			(newValue) => {
				if (animationId) {
					cancelAnimationFrame(animationId);
				}

				const id = requestAnimationFrame(() => {
					setDisplayValue(newValue);
				});

				setAnimationId(id);
			},
			[animationId],
		);

		// Keyboard support
		const handleKeyDown = useCallback(
			(e) => {
				if (!sliderRef.current) return;

				let newValue = displayValue;
				const smallStep = step;
				const largeStep = Math.max(step * 10, (max - min) / 10);

				switch (e.key) {
					case "ArrowRight":
					case "ArrowUp":
						e.preventDefault();
						newValue = Math.min(max, displayValue + smallStep);
						break;
					case "ArrowLeft":
					case "ArrowDown":
						e.preventDefault();
						newValue = Math.max(min, displayValue - smallStep);
						break;
					case "PageUp":
						e.preventDefault();
						newValue = Math.min(max, displayValue + largeStep);
						break;
					case "PageDown":
						e.preventDefault();
						newValue = Math.max(min, displayValue - largeStep);
						break;
					case "Home":
						e.preventDefault();
						newValue = min;
						break;
					case "End":
						e.preventDefault();
						newValue = max;
						break;
					default:
						return;
				}

				// Round to nearest step
				newValue = Math.round(newValue / step) * step;
				newValue = Math.max(min, Math.min(max, newValue));

				setDisplayValue(newValue);
				onChange(newValue);
			},
			[displayValue, min, max, step, onChange],
		);

		const handleMouseDown = useCallback(
			(e) => {
				// Only handle left mouse button
				if (e.button !== 0) return;

				e.preventDefault();
				e.stopPropagation();

				setIsDragging(true);
				dragStartValueRef.current = value;

				// Disable text selection during drag
				document.body.style.userSelect = "none";
				document.body.style.webkitUserSelect = "none";

				const newValue = calculateValue(e.clientX);
				updateVisualValue(newValue);

				const handleMouseMove = (moveEvent) => {
					moveEvent.preventDefault();
					moveEvent.stopPropagation();

					const currentValue = calculateValue(moveEvent.clientX);
					updateVisualValue(currentValue);
				};

				const handleMouseUp = (upEvent) => {
					upEvent.preventDefault();
					upEvent.stopPropagation();

					// Re-enable text selection
					document.body.style.userSelect = "";
					document.body.style.webkitUserSelect = "";

					setIsDragging(false);

					// Final value calculation and callback
					const finalValue = calculateValue(upEvent.clientX);
					setDisplayValue(finalValue);

					// Only call onChange if value actually changed
					if (finalValue !== dragStartValueRef.current) {
						onChange(finalValue);
					}

					// Clean up event listeners
					document.removeEventListener("mousemove", handleMouseMove, {
						passive: false,
					});
					document.removeEventListener("mouseup", handleMouseUp);

					// Cancel any pending animation frame
					if (animationId) {
						cancelAnimationFrame(animationId);
						setAnimationId(null);
					}
				};

				// Use passive: false to ensure preventDefault works
				document.addEventListener("mousemove", handleMouseMove, {
					passive: false,
				});
				document.addEventListener("mouseup", handleMouseUp);
			},
			[calculateValue, onChange, value, updateVisualValue, animationId],
		);

		const handleThumbMouseDown = useCallback(
			(e) => {
				// Stop event from bubbling to track
				e.stopPropagation();
				handleMouseDown(e);
			},
			[handleMouseDown],
		);

		const handleTrackClick = useCallback(
			(e) => {
				// Only handle clicks directly on the track, not on thumb
				if (e.target === e.currentTarget && !isDragging) {
					e.preventDefault();
					e.stopPropagation();

					const newValue = calculateValue(e.clientX);
					setDisplayValue(newValue);
					onChange(newValue);
				}
			},
			[calculateValue, onChange, isDragging],
		);

		// Cleanup on unmount
		useEffect(() => {
			return () => {
				if (animationId) {
					cancelAnimationFrame(animationId);
				}
				// Restore text selection if component unmounts during drag
				document.body.style.userSelect = "";
				document.body.style.webkitUserSelect = "";
			};
		}, [animationId]);

		const percentage = ((displayValue - min) / (max - min)) * 100;
		const formattedValue = formatValue
			? formatValue(displayValue)
			: `${displayValue}${unit}`;

		return (
			<div className="enhanced-slider-container">
				<label className="enhanced-slider-label">
					{label}: {formattedValue}
				</label>

				<div className="enhanced-slider-wrapper">
					<div
						ref={sliderRef}
						className={`enhanced-slider-track ${isDragging ? "dragging" : ""}`}
						onClick={handleTrackClick}
						onMouseDown={handleMouseDown}
						onKeyDown={handleKeyDown}
						role="slider"
						aria-valuemin={min}
						aria-valuemax={max}
						aria-valuenow={displayValue}
						aria-label={label}
						tabIndex={0}
					>
						{/* Progress fill */}
						<div
							className="enhanced-slider-fill"
							style={{ width: `${percentage}%` }}
						/>

						{/* Thumb - now fully interactive */}
						<div
							className={`enhanced-slider-thumb ${isDragging ? "dragging" : ""}`}
							style={{ left: `${percentage}%` }}
							onMouseDown={handleThumbMouseDown}
						/>
					</div>

					{/* Range labels */}
					<div className="enhanced-slider-labels">
						<span>
							{min}
							{unit}
						</span>
						<span>
							{max}
							{unit}
						</span>
					</div>
				</div>
			</div>
		);
	},
);

const LimitInput = React.memo(({ value, onChange }) => {
	const [localValue, setLocalValue] = useState(value || 1000);

	useEffect(() => {
		setLocalValue(value || 1000);
	}, [value]);

	const handleChange = useCallback((e) => {
		const newValue = e.target.value;
		setLocalValue(newValue);
	}, []);

	const handleBlur = useCallback(() => {
		const numValue = parseInt(localValue, 10);
		const finalValue = isNaN(numValue) ? 1000 : Math.max(1, numValue);
		if (finalValue !== value) {
			onChange(finalValue);
		}
	}, [localValue, value, onChange]);

	const handleKeyPress = useCallback(
		(e) => {
			if (e.key === "Enter") {
				handleBlur();
			}
		},
		[handleBlur],
	);

	return (
		<input
			type="number"
			value={localValue}
			onChange={handleChange}
			onBlur={handleBlur}
			onKeyPress={handleKeyPress}
			placeholder="1000"
			min="1"
			style={{
				width: "100%",
				padding: "6px 8px",
				border: "1px solid #d1d5db",
				borderRadius: "4px",
				fontSize: "11px",
				backgroundColor: "white",
				color: "#374151",
				boxSizing: "border-box",
			}}
		/>
	);
});

// Custom Filter Input Component to fix refreshing issue
const CustomFilterInput = React.memo(({ customFilter, onUpdate }) => {
	// Columns that need to be multiplied by 10^6
	const columnsToMultiply = [
		"nonzerobidsvalueinusd",
		"bidspendinusd",
		"revenueperimpression",
		"costperimpression",
		"bid_guidance",
		"bidCpm",
	];

	// Display value (divide by 10^6 if it's one of the special columns)
	const displayValue = columnsToMultiply.includes(customFilter.column)
		? (customFilter.threshold || 0) / 10 ** 6
		: customFilter.threshold || 0;

	const [localValue, setLocalValue] = useState(displayValue);

	useEffect(() => {
		setLocalValue(displayValue);
	}, [displayValue]);

	const handleChange = useCallback((e) => {
		setLocalValue(e.target.value);
	}, []);

	const handleBlur = useCallback(() => {
		const value = localValue === "" ? 0 : parseFloat(localValue);
		const finalValue = isNaN(value) ? 0 : value;

		// Pass the original value (without multiplication) - multiplication happens in updateCustomFilter
		if (finalValue !== displayValue) {
			onUpdate(customFilter.id, "threshold", finalValue);
		}
	}, [localValue, displayValue, customFilter.id, onUpdate]);

	const handleKeyPress = useCallback(
		(e) => {
			if (e.key === "Enter") {
				handleBlur();
			}
		},
		[handleBlur],
	);

	return (
		<input
			type="number"
			value={localValue}
			onChange={handleChange}
			onBlur={handleBlur}
			onKeyPress={handleKeyPress}
			placeholder="0"
			step="0.000001"
			style={{
				width: "100%",
				padding: "4px 6px",
				fontSize: "10px",
				border: "1px solid #ddd",
				borderRadius: "3px",
				backgroundColor: "white",
				boxSizing: "border-box",
			}}
		/>
	);
});

// FIXED: Helper function to decode language code to readable name - enhanced with backend data
const decodeLanguage = (languageCode) => {
	if (!languageCode) return "Unknown";

	const cleanCode = languageCode
		.toString()
		.trim()
		.replace(/^["']|["']$/g, "");
	const upperCode = cleanCode.toUpperCase();

	// Look up the language name
	const decodedName = LANGUAGE_CODES[upperCode];

	// Return decoded name if found, otherwise return the cleaned code
	return decodedName || cleanCode;
};

const BurgerIcon = () => (
	<svg
		width="20"
		height="20"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<line x1="3" y1="6" x2="21" y2="6"></line>
		<line x1="3" y1="12" x2="21" y2="12"></line>
		<line x1="3" y1="18" x2="21" y2="18"></line>
	</svg>
);

const UploadIcon = () => (
	<svg
		width="14"
		height="14"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
		<polyline points="17,8 12,3 7,8"></polyline>
		<line x1="12" y1="3" x2="12" y2="15"></line>
	</svg>
);

const RemoveIcon = () => (
	<svg
		width="12"
		height="12"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<line x1="18" y1="6" x2="6" y2="18"></line>
		<line x1="6" y1="6" x2="18" y2="18"></line>
	</svg>
);

const SectionHeader = ({
	title,
	isExpanded,
	onToggle,
	hasActiveFilters = false,
	style = {},
}) => (
	<div
		className={`section-header ${isExpanded ? "expanded" : "collapsed"}`}
		onClick={onToggle}
		style={style}
	>
		<span className="section-title">{title}</span>
		<div className="section-indicators">
			<ChevronDownIcon />
		</div>
	</div>
);

export const AppFiltersSidebar = ({
	showFilters,
	filters,
	filterOptions,
	expandedSections,
	onToggleSection,
	onUpdateFilter,
	onToggleArrayFilter,
	onClearAllFilters,
	onClose,
	onUploadIncludeList,
	onUploadBlockedList,
}) => {
	// FIXED: Use the hook to get access to enhanced methods
	const {
		updateRatingFilter, // FIXED: New method for rating updates
		hasBackendRatings,
		hasBackendRatingCounts,
		backendDataStats,
	} = useAppFilters();

	// Scroll position preservation
	const filtersContentRef = useRef(null);
	const savedScrollPositions = useRef({});

	const [publisherSearchInput, setPublisherSearchInput] = useState("");

	// Add debounced publisher search
	const [debouncedPublisherSearch, setDebouncedPublisherSearch] = useState("");

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedPublisherSearch(publisherSearchInput);
		}, 300);

		return () => clearTimeout(timer);
	}, [publisherSearchInput]);

	// Save scroll position before re-render
	const saveScrollPosition = useCallback(() => {
		if (filtersContentRef.current) {
			savedScrollPositions.current.main = filtersContentRef.current.scrollTop;
		}

		const scrollableElements = filtersContentRef.current?.querySelectorAll(
			".countries-by-region, .search-results-list, .categories-list, .publisher-list",
		);
		scrollableElements?.forEach((element, index) => {
			savedScrollPositions.current[`section_${index}`] = element.scrollTop;
		});
	}, []);

	// Restore scroll position after re-render
	const restoreScrollPosition = useCallback(() => {
		setTimeout(() => {
			if (
				filtersContentRef.current &&
				savedScrollPositions.current.main !== undefined
			) {
				filtersContentRef.current.scrollTop = savedScrollPositions.current.main;
			}

			const scrollableElements = filtersContentRef.current?.querySelectorAll(
				".countries-by-region, .search-results-list, .categories-list, .publisher-list",
			);
			scrollableElements?.forEach((element, index) => {
				if (savedScrollPositions.current[`section_${index}`] !== undefined) {
					element.scrollTop = savedScrollPositions.current[`section_${index}`];
				}
			});
		}, 0);
	}, []);

	// FIXED: Enhanced filter handlers with backend data awareness
	const handleAppUpdateFilter = useCallback(
		(filterKey, value) => {
			saveScrollPosition();

			// FIXED: Use smart rating update for rating fields
			if (
				(filterKey === "minRating" && hasBackendRatings) ||
				(filterKey === "minRatingCount" && hasBackendRatingCounts)
			) {
				if (updateRatingFilter) {
					updateRatingFilter(filterKey, value);
				} else {
					onUpdateFilter(filterKey, value);
				}
			} else {
				onUpdateFilter(filterKey, value);
			}
		},
		[
			onUpdateFilter,
			updateRatingFilter,
			hasBackendRatings,
			hasBackendRatingCounts,
			saveScrollPosition,
		],
	);

	// Ensure default custom filters are always present
	const ensuredFilters = {
		...filters,
		customFilters:
			filters.customFilters && filters.customFilters.length > 0
				? filters.customFilters
				: [
						{
							id: Date.now(),
							column: "cprmarketrequests",
							threshold: 10000,
						},
						{
							id: Date.now() + 1,
							column: "impressions",
							threshold: 100,
						},
					],
	};

	// Custom filter management
	// Update the updateCustomFilter function
	const updateCustomFilter = useCallback(
		(id, field, value) => {
			const currentFilters = ensuredFilters.customFilters || [];

			// Columns that need to be multiplied by 10^6
			const columnsToMultiply = [
				"nonzerobidsvalueinusd",
				"bidspendinusd",
				"revenueperimpression",
				"costperimpression",
				"bid_guidance",
				"bidCpm",
			];

			const updatedFilters = currentFilters.map((filter) => {
				if (filter.id === id) {
					const updatedFilter = { ...filter, [field]: value };

					// If updating threshold for columns that need multiplication
					if (
						field === "threshold" &&
						columnsToMultiply.includes(filter.column)
					) {
						return {
							...updatedFilter,
							threshold: value * 10 ** 6,
						};
					}

					return updatedFilter;
				}
				return filter;
			});

			handleAppUpdateFilter("customFilters", updatedFilters);
		},
		[ensuredFilters.customFilters, handleAppUpdateFilter],
	);

	const addCustomFilter = useCallback(() => {
		const currentFilters = ensuredFilters.customFilters || [];
		const newFilter = {
			id: Date.now() + Math.random(),
			column: "cprmarketrequests",
			threshold: 0,
		};

		const updatedFilters = [...currentFilters, newFilter];
		handleAppUpdateFilter("customFilters", updatedFilters);
	}, [ensuredFilters.customFilters, handleAppUpdateFilter]);

	const removeCustomFilter = useCallback(
		(id) => {
			const currentFilters = ensuredFilters.customFilters || [];
			const filterToRemove = currentFilters.find((filter) => filter.id === id);

			const isDefaultFilter =
				filterToRemove &&
				(filterToRemove.column === "cprmarketrequests" ||
					filterToRemove.column === "impressions");

			if (isDefaultFilter) {
				const updatedFilters = currentFilters.map((filter) =>
					filter.id === id ? { ...filter, threshold: 0 } : filter,
				);
				handleAppUpdateFilter("customFilters", updatedFilters);
			} else {
				const updatedFilters = currentFilters.filter(
					(filter) => filter.id !== id,
				);
				handleAppUpdateFilter("customFilters", updatedFilters);
			}
		},
		[ensuredFilters.customFilters, handleAppUpdateFilter],
	);

	const handleAppToggleArrayFilter = useCallback(
		(filterKey, value) => {
			saveScrollPosition();

			if (onToggleArrayFilter) {
				onToggleArrayFilter(filterKey, value);
			}
		},
		[onToggleArrayFilter, saveScrollPosition],
	);

	// Add publisher search handlers
	const handlePublisherSearchChange = useCallback((value) => {
		setPublisherSearchInput(value);
	}, []);

	// Restore scroll position after filter changes
	useEffect(() => {
		restoreScrollPosition();
	}, [filters, restoreScrollPosition]);

	const [developerSearch, setDeveloperSearch] = useState("");
	const [countrySearch, setCountrySearch] = useState("");
	const [publisherSearch, setPublisherSearch] = useState("");

	// Upload states management
	const [uploadStates, setUploadStates] = useState({
		includeList: {
			isUploading: false,
			fileName: null,
			bundleIds: [],
			error: null,
			isUploaded: false,
		},
		blockedList: {
			isUploading: false,
			fileName: null,
			bundleIds: [],
			error: null,
			isUploaded: false,
		},
	});

	const [showUploadTooltip, setShowUploadTooltip] = useState(false);

	// State for collapsible categories
	const [expandedCategories, setExpandedCategories] = useState({
		targeting: false,
		appCategory: false,
		appMetadata: false,
		contentSafety: false,
		appPopularity: false,
		performance: false,
		publishers: false,
	});

	// State for expanded regions in geography
	const [expandedRegions, setExpandedRegions] = useState({});

	// CSV parsing utility function (keeping original)
	const parseCSV = (csvText) => {
		const lines = csvText
			.split("\n")
			.map((line) => line.trim())
			.filter((line) => line);
		const domains = [];

		for (let i = 0; i < lines.length; i++) {
			const line = lines[i];

			if (
				i === 0 &&
				(line.toLowerCase().includes("domain") ||
					line.toLowerCase().includes("website") ||
					line.toLowerCase().includes("site"))
			) {
				continue;
			}

			const values = line
				.split(",")
				.map((val) => val.trim().replace(/^["']|["']$/g, ""));

			for (const value of values) {
				if (value && isValidDomain(value)) {
					domains.push(cleanDomain(value));
					break;
				}
			}
		}

		return domains;
	};

	const isValidDomain = (domain) => {
		if (!domain || typeof domain !== "string") return false;
		const domainRegex =
			/^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
		const cleanDomain = domain
			.toLowerCase()
			.replace(/^https?:\/\//, "")
			.replace(/^www\./, "")
			.replace(/\/.*$/, "")
			.trim();
		return cleanDomain.length > 0 && domainRegex.test(cleanDomain);
	};

	const cleanDomain = (domain) => {
		return domain
			.toLowerCase()
			.replace(/^https?:\/\//, "")
			.replace(/^www\./, "")
			.replace(/\/.*$/, "")
			.trim();
	};

	const parseUploadedFile = (fileContent) => {
		const lines = fileContent
			.split("\n")
			.map((line) => line.trim())
			.filter((line) => line);
		const bundleIds = [];

		if (lines.length === 0) return bundleIds;

		const parseCSVLine = (line) => {
			const result = [];
			let current = "";
			let inQuotes = false;

			for (let i = 0; i < line.length; i++) {
				const char = line[i];

				if (char === '"') {
					inQuotes = !inQuotes;
				} else if (char === "," && !inQuotes) {
					result.push(current.trim());
					current = "";
				} else {
					current += char;
				}
			}
			result.push(current.trim());
			return result;
		};

		const headers = parseCSVLine(lines[0]).map((h) =>
			h.toLowerCase().replace(/['"]/g, ""),
		);

		const bundleIdColumnNames = [
			"bundle_id",
			"bundleid",
			"bundle",
			"package_name",
			"packagename",
			"app_id",
			"appid",
			"id",
			"bundle_identifier",
			"package",
			"app_bundle_id",
		];

		let bundleIdColumnIndex = -1;

		for (const possibleName of bundleIdColumnNames) {
			bundleIdColumnIndex = headers.findIndex(
				(header) =>
					header.includes(possibleName) || possibleName.includes(header),
			);
			if (bundleIdColumnIndex !== -1) {
				console.log(
					`Found bundle ID column: "${headers[bundleIdColumnIndex]}" at index ${bundleIdColumnIndex}`,
				);
				break;
			}
		}

		if (bundleIdColumnIndex === -1) {
			console.log(
				"No bundle ID column found by name, checking data patterns...",
			);

			for (let colIndex = 0; colIndex < headers.length; colIndex++) {
				let likelyBundleIds = 0;
				const samplesToCheck = Math.min(5, lines.length - 1);

				for (let rowIndex = 1; rowIndex <= samplesToCheck; rowIndex++) {
					const rowData = parseCSVLine(lines[rowIndex]);
					const cellValue = rowData[colIndex]?.replace(/['"]/g, "").trim();

					if (
						cellValue &&
						(cellValue.includes(".") ||
							cellValue.includes("_") ||
							/^[a-zA-Z][a-zA-Z0-9._-]*[a-zA-Z0-9]$/.test(cellValue))
					) {
						likelyBundleIds++;
					}
				}

				if (likelyBundleIds > samplesToCheck / 2) {
					bundleIdColumnIndex = colIndex;
					console.log(
						`Found likely bundle ID column at index ${colIndex}: "${headers[colIndex]}"`,
					);
					break;
				}
			}
		}

		if (bundleIdColumnIndex === -1) {
			bundleIdColumnIndex = 0;
			console.log(
				`No bundle ID column identified, using first column: "${headers[0]}"`,
			);
		}

		for (let i = 1; i < lines.length; i++) {
			const rowData = parseCSVLine(lines[i]);
			const bundleId = rowData[bundleIdColumnIndex]
				?.replace(/['"]/g, "")
				.trim();

			if (bundleId && bundleId.length > 0) {
				if (
					!/^\d+$/.test(bundleId) &&
					!["name", "title", "description", "category"].includes(
						bundleId.toLowerCase(),
					)
				) {
					bundleIds.push(bundleId);
				}
			}
		}

		const uniqueBundleIds = [...new Set(bundleIds)].sort();

		console.log(`CSV parsing results:`, {
			totalLines: lines.length,
			headerColumns: headers,
			bundleIdColumn: headers[bundleIdColumnIndex],
			bundleIdColumnIndex,
			extractedBundleIds: uniqueBundleIds.length,
			sampleBundleIds: uniqueBundleIds.slice(0, 5),
		});

		return uniqueBundleIds;
	};

	const handleFileUpload = async (file, listType) => {
		if (!file) return;

		if (!file.name.toLowerCase().endsWith(".csv")) {
			setUploadStates((prev) => ({
				...prev,
				[listType]: {
					...prev[listType],
					error: "Please upload a CSV file",
					isUploading: false,
				},
			}));
			return;
		}

		setUploadStates((prev) => ({
			...prev,
			[listType]: {
				...prev[listType],
				isUploading: true,
				error: null,
			},
		}));

		try {
			const fileContent = await new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = (e) => resolve(e.target.result);
				reader.onerror = reject;
				reader.readAsText(file);
			});

			const bundleIds = parseUploadedFile(fileContent);

			if (bundleIds.length === 0) {
				throw new Error("No valid bundle IDs found in the CSV file");
			}

			setUploadStates((prev) => ({
				...prev,
				[listType]: {
					isUploading: false,
					fileName: file.name,
					bundleIds: bundleIds,
					error: null,
					isUploaded: true,
				},
			}));

			if (listType === "includeList") {
				handleAppUpdateFilter("uploadedBundles", {
					includeList: bundleIds,
					excludeList: uploadStates.blockedList.bundleIds || [],
				});
			} else if (listType === "blockedList") {
				handleAppUpdateFilter("uploadedBundles", {
					includeList: uploadStates.includeList.bundleIds || [],
					excludeList: bundleIds,
				});
			}

			console.log(`${listType} updated with ${bundleIds.length} bundle IDs`);
		} catch (error) {
			console.error("File upload error:", error);
			setUploadStates((prev) => ({
				...prev,
				[listType]: {
					isUploading: false,
					fileName: null,
					bundleIds: [],
					error: error.message,
					isUploaded: false,
				},
			}));
		}
	};

	const clearUploadedList = (listType) => {
		setUploadStates((prev) => ({
			...prev,
			[listType]: {
				isUploading: false,
				fileName: null,
				bundleIds: [],
				error: null,
				isUploaded: false,
			},
		}));

		if (listType === "includeList") {
			handleAppUpdateFilter("uploadedBundles", {
				includeList: [],
				excludeList: uploadStates.blockedList.bundleIds || [],
			});
		} else if (listType === "blockedList") {
			handleAppUpdateFilter("uploadedBundles", {
				includeList: uploadStates.includeList.bundleIds || [],
				excludeList: [],
			});
		}
	};

	const toggleCategory = (category) => {
		setExpandedCategories((prev) => {
			const newState = {
				...prev,
				[category]: !prev[category],
			};

			const categorySections = {
				targeting: [
					"platform",
					"mediaSubtype",
					"deviceType",
					"videoPlacement",
					"geography",
				],
				appCategory: ["categories"],
				appMetadata: [
					"developer",
					"languages",
					"appStatus",
					"appType",
					"features",
				],
				contentSafety: ["contentRating", "safetyTier", "coppa"],
				appPopularity: ["appRating", "downloads", "appAge", "lastUpdated"],
				performance: ["ctr", "viewability", "reach", "relevance"],
			};

			const sectionsInCategory = categorySections[category] || [];

			if (prev[category]) {
				sectionsInCategory.forEach((section) => {
					onToggleSection(section, false);
				});
			} else {
				sectionsInCategory.forEach((section) => {
					onToggleSection(section, true);
				});
			}

			return newState;
		});
	};

	const toggleRegion = (region) => {
		setExpandedRegions((prev) => ({
			...prev,
			[region]: !prev[region],
		}));
	};

	if (!showFilters) return null;

	// Define country presets
	const countryPresets = {
		US: {
			label: "United States",
			countries: ["US"],
		},
		UK: {
			label: "United Kingdom",
			countries: ["GB"],
		},
		APAC: {
			label: "Asia Pacific",
			countries: [
				"AU",
				"JP",
				"KR",
				"SG",
				"HK",
				"TW",
				"TH",
				"MY",
				"ID",
				"PH",
				"VN",
				"IN",
				"CN",
				"NZ",
			],
		},
	};

	const allCountryCodes = Object.keys(COUNTRY_CODES_MAP).filter((code) => {
		const countryInfo = getCountryInfo(code);
		return countryInfo.region && countryInfo.region.trim() !== "";
	});
	const countriesByRegion = groupCountriesByRegion(allCountryCodes);
	const sortedRegions = sortRegions(Object.keys(countriesByRegion));

	// Geography logic
	const handleGlobalSelection = (e) => {
		const currentSelection = filters.geography || [];
		if (e.target.checked) {
			allCountryCodes.forEach((country) => {
				if (!currentSelection.includes(country)) {
					handleAppToggleArrayFilter("geography", country);
				}
			});
		} else {
			allCountryCodes.forEach((country) => {
				if (currentSelection.includes(country)) {
					handleAppToggleArrayFilter("geography", country);
				}
			});
		}
	};

	const handleRegionSelection = (region, e) => {
		const regionCountries = countriesByRegion[region] || [];
		const currentSelection = filters.geography || [];
		if (e.target.checked) {
			regionCountries.forEach((country) => {
				if (!currentSelection.includes(country)) {
					handleAppToggleArrayFilter("geography", country);
				}
			});
		} else {
			regionCountries.forEach((country) => {
				if (currentSelection.includes(country)) {
					handleAppToggleArrayFilter("geography", country);
				}
			});
		}
	};

	// Add filtered publishers logic
	const getFilteredPublishers = () => {
		const allPublishers = filterOptions?.publishers || [];

		if (
			debouncedPublisherSearch &&
			debouncedPublisherSearch.trim().length > 0
		) {
			return allPublishers.filter((pub) =>
				pub.toLowerCase().includes(debouncedPublisherSearch.toLowerCase()),
			);
		}

		return allPublishers.length > 50
			? allPublishers.slice(0, 50)
			: allPublishers;
	};

	const filteredPublishers = getFilteredPublishers();

	// Handle preset country selection
	const handlePresetSelection = (presetKey) => {
		const preset = countryPresets[presetKey];
		const currentSelection = filters.geography || [];
		const allSelected = preset.countries.every((country) =>
			currentSelection.includes(country),
		);
		if (allSelected) {
			preset.countries.forEach((country) => {
				if (currentSelection.includes(country)) {
					handleAppToggleArrayFilter("geography", country);
				}
			});
		} else {
			preset.countries.forEach((country) => {
				if (!currentSelection.includes(country)) {
					handleAppToggleArrayFilter("geography", country);
				}
			});
		}
	};

	// Check if preset is selected
	const isPresetSelected = (presetKey) => {
		const preset = countryPresets[presetKey];
		const currentSelection = filters.geography || [];
		return preset.countries.every((country) =>
			currentSelection.includes(country),
		);
	};

	// Check if preset is partially selected
	const isPresetPartiallySelected = (presetKey) => {
		const preset = countryPresets[presetKey];
		const currentSelection = filters.geography || [];
		const selectedInPreset = preset.countries.filter((country) =>
			currentSelection.includes(country),
		);
		return (
			selectedInPreset.length > 0 &&
			selectedInPreset.length < preset.countries.length
		);
	};

	const isGlobalSelected = () => {
		return filters.geography?.length === allCountryCodes.length;
	};

	const isRegionSelected = (region) => {
		const regionCountries = countriesByRegion[region] || [];
		const currentSelection = filters.geography || [];
		return (
			regionCountries.length > 0 &&
			regionCountries.every((country) => currentSelection.includes(country))
		);
	};

	const isRegionPartiallySelected = (region) => {
		const regionCountries = countriesByRegion[region] || [];
		const currentSelection = filters.geography || [];
		const selectedInRegion = regionCountries.filter((country) =>
			currentSelection.includes(country),
		);
		return (
			selectedInRegion.length > 0 &&
			selectedInRegion.length < regionCountries.length
		);
	};

	// FIXED: Use backend data for platforms, content ratings, etc.
	const platforms =
		filterOptions?.platforms?.length > 0
			? filterOptions.platforms
			: ["ios", "android", "cross-platform"];

	const appStatuses =
		filterOptions?.marketStatuses?.length > 0
			? filterOptions.marketStatuses
			: ["Published", "Unpublished", "Suspended", "Pre-release"];

	const contentRatings =
		filterOptions?.contentRatings?.length > 0
			? filterOptions.contentRatings
			: [
					"4+",
					"+12",
					"Teen",
					"General",
					"Medium Maturity",
					"Teen Drug Reference,",
					"Teen Mild Violence,",
					"Everyone",
					"Everyone 10+",
					"Mature 17+",
					"Guidance Suggested",
					"Restricted",
				];

	const appFeatures =
		filterOptions?.features?.length > 0
			? filterOptions.features
			: [
					"In-App Purchases",
					"Users Interact",
					"Shares Location",
					"Shares Info",
					"Digital Purchases",
					"Push Notifications",
					"Offline Viewing",
					"Social Features",
					"Camera Access",
					"Microphone Access",
				];

	// FIXED: Use backend data for available columns or fallback
	const availableColumns =
		filterOptions?.availableColumns?.length > 0
			? filterOptions.availableColumns
			: [
					{ value: "cprmarketrequests", label: "CPR Market Requests" },
					{ value: "impressions", label: "Total Impressions" },
					{ value: "clicks", label: "Clicks" },
					{ value: "viewables", label: "Viewables" },
					{ value: "viewabilitymeasures", label: "Viewability Measures" },
					{ value: "completefirstoccurrence", label: "Video Completions" },
					{ value: "startfirstoccurrence", label: "Video Starts" },
				];

	// Filter developers - show top 5 by default, all when searching
	const getFilteredDevelopers = () => {
		const allDevelopers = filterOptions?.developers || [];

		if (developerSearch.length > 0) {
			return allDevelopers.filter((dev) =>
				dev.toLowerCase().includes(developerSearch.toLowerCase()),
			);
		}

		return allDevelopers.slice(0, 5);
	};
	const filteredDevelopers = getFilteredDevelopers();

	// Filter countries based on search
	const getFilteredCountries = () => {
		if (!countrySearch.trim()) return null;
		return allCountryCodes.filter((countryCode) => {
			const countryInfo = getCountryInfo(countryCode);
			return (
				countryInfo.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
				countryCode.toLowerCase().includes(countrySearch.toLowerCase()) ||
				countryInfo.region.toLowerCase().includes(countrySearch.toLowerCase())
			);
		});
	};

	const filteredCountries = getFilteredCountries();

	// FIXED: Get unique languages from API response with enhanced decoding
	const getAvailableLanguages = () => {
		const languageCodes = filterOptions?.languages || [];
		return languageCodes
			.map((code) => {
				const decodedName = decodeLanguage(code);
				return {
					code: code,
					name: decodedName,
					displayName: decodedName === code ? code : `${decodedName} (${code})`,
				};
			})
			.sort((a, b) => a.name.localeCompare(b.name));
	};

	const availableLanguages = getAvailableLanguages();

	const CategoryHeader = ({ title, isExpanded, onClick, children }) => (
		<div className="filter-category-box">
			<div
				className={`category-header ${isExpanded ? "expanded" : "collapsed"}`}
				onClick={onClick}
			>
				<h3 className="category-title-new">{title}</h3>
				<div
					className={`category-chevron ${isExpanded ? "expanded" : "collapsed"}`}
				>
					<ChevronDownIcon />
				</div>
			</div>
			{isExpanded && (
				<div
					className="category-content"
					style={{ paddingLeft: "8px", paddingRight: "8px" }}
				>
					{children}
				</div>
			)}
		</div>
	);

	return (
		<div className="filters-sidebar">
			<div className="filters-header">
				<h2 className="filters-title" style={{ fontSize: "16px" }}>
					App Filters
				</h2>
				<div className="filters-actions">
					<button
						className="btn-medium-primary"
						onClick={onClearAllFilters}
						style={{ fontSize: "12px" }}
					>
						Clear All
					</button>
					<button onClick={onClose} className="close-btn">
						<BurgerIcon />
					</button>
				</div>
			</div>
			<div className="filters-content" ref={filtersContentRef}>
				{/* 1. Targeting Filters */}
				<CategoryHeader
					title="Targeting Filters"
					isExpanded={expandedCategories.targeting}
					onClick={() => toggleCategory("targeting")}
				>
					{/* Platform */}
					<div className="filter-section">
						<SectionHeader
							title="Platform"
							isExpanded={expandedSections.platform}
							onToggle={() => onToggleSection("platform")}
							hasActiveFilters={filters.platform?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.platform && (
							<div className="section-content">
								<div className="platform-tags">
									{platforms.map((platform) => (
										<span
											key={platform}
											className={`tag tag-clickable ${filters.platform?.includes(platform) ? "tag-selected" : "tag-gray"}`}
											onClick={() =>
												handleAppToggleArrayFilter("platform", platform)
											}
											style={{ fontSize: "10px" }}
										>
											{platform}
										</span>
									))}
								</div>
							</div>
						)}
					</div>

					{/* Media Subtype */}
					<div className="filter-section">
						<SectionHeader
							title="Media Subtype"
							isExpanded={expandedSections.mediaSubtype}
							onToggle={() => onToggleSection("mediaSubtype")}
							hasActiveFilters={filters.mediaSubtype?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.mediaSubtype && (
							<div className="section-content">
								<div className="media-subtype-tags">
									{(
										filterOptions?.mediaSubtypes || [
											"Display",
											"Video",
											"Native",
											"Display+Video",
											"Display+Native",
											"Video+Native",
										]
									).map((type) => (
										<span
											key={type}
											className={`tag tag-clickable ${filters.mediaSubtype?.includes(type) ? "tag-selected" : "tag-gray"}`}
											onClick={() =>
												handleAppToggleArrayFilter("mediaSubtype", type)
											}
											style={{ fontSize: "10px" }}
										>
											{type}
										</span>
									))}
								</div>
							</div>
						)}
					</div>

					{/* Device Type */}
					<div className="filter-section">
						<SectionHeader
							title="Device Type"
							isExpanded={expandedSections.deviceTypes}
							onToggle={() => onToggleSection("deviceTypes")}
							hasActiveFilters={filters.deviceTypes?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.deviceTypes && (
							<div className="section-content">
								<div className="device-type-tags">
									{(
										filterOptions?.deviceTypes || [
											"phone",
											"tablet",
											"desktop",
											"connected tv",
											"connected device",
											"mobile/tablet",
											"set-top-box",
											"dooh",
										]
									).map((device) => (
										<span
											key={device}
											className={`tag tag-clickable ${filters.deviceTypes?.includes(device) ? "tag-selected" : "tag-gray"}`}
											onClick={() =>
												handleAppToggleArrayFilter("deviceTypes", device)
											}
											style={{ fontSize: "10px" }}
										>
											{device}
										</span>
									))}
								</div>
							</div>
						)}
					</div>

					{/* Video Placement */}
					<div className="filter-section">
						<SectionHeader
							title="Video Placement"
							isExpanded={expandedSections.videoPlacement}
							onToggle={() => onToggleSection("videoPlacement")}
							hasActiveFilters={filters.videoPlacement?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.videoPlacement && (
							<div className="section-content">
								<div className="video-placement-tags">
									{(
										filterOptions?.videoPlacementTypes || [
											"INSTREAM",
											"OUTSTREAM",
											"REWARDED",
											"INTERSTITIAL",
											"ACCOMPANYING_CONTENT",
											"NO_CONTENT_STANDALONE",
										]
									).map((placement) => (
										<span
											key={placement}
											className={`tag tag-clickable ${filters.videoPlacement?.includes(placement) ? "tag-selected" : "tag-gray"}`}
											onClick={() =>
												handleAppToggleArrayFilter("videoPlacement", placement)
											}
											style={{ fontSize: "10px" }}
										>
											{placement}
										</span>
									))}
								</div>
							</div>
						)}
					</div>

					{/* Geography */}
					<div className="filter-section">
						<SectionHeader
							title="Geography"
							isExpanded={expandedSections.geography}
							onToggle={() => onToggleSection("geography")}
							hasActiveFilters={filters.geography?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.geography && (
							<div className="section-content">
								<div className="geography-section">
									<div className="geography-search">
										<div className="search-container">
											<input
												key="country-search"
												type="text"
												placeholder="Search countries..."
												value={countrySearch}
												onChange={(e) => setCountrySearch(e.target.value)}
												className="search-input"
												style={{ fontSize: "11px" }}
												autoComplete="off"
											/>
											{countrySearch && (
												<button
													onClick={() => setCountrySearch("")}
													className="clear-search-btn"
													type="button"
												>
													×
												</button>
											)}
										</div>
									</div>
									{filteredCountries && (
										<div className="geography-search-results">
											<div className="search-results-header">
												<span style={{ fontSize: "10px", color: "#6b7280" }}>
													Found {filteredCountries.length} countries matching "
													{countrySearch}"
												</span>
											</div>
											<div className="search-results-list">
												{filteredCountries.map((countryCode) => {
													const countryInfo = getCountryInfo(countryCode);
													return (
														<label
															key={countryCode}
															className="checkbox-label country-search-result"
															style={{ fontSize: "10px" }}
														>
															<input
																type="checkbox"
																checked={
																	filters.geography?.includes(countryCode) ||
																	false
																}
																onChange={() =>
																	handleAppToggleArrayFilter(
																		"geography",
																		countryCode,
																	)
																}
															/>
															<span className="checkbox-text">
																{countryInfo.name}
																<span className="country-code-hint">
																	({countryCode})
																</span>
															</span>
														</label>
													);
												})}
												{filteredCountries.length === 0 && (
													<div
														className="no-results"
														style={{ fontSize: "10px" }}
													>
														No countries found matching "{countrySearch}"
													</div>
												)}
											</div>
										</div>
									)}
									{!filteredCountries && (
										<>
											<div className="presets-buttons">
												{Object.entries(countryPresets).map(([key, preset]) => {
													const isSelected = isPresetSelected(key);
													const isPartial = isPresetPartiallySelected(key);
													return (
														<button
															key={key}
															className={`preset-btn ${isSelected ? "selected" : ""} ${isPartial ? "partial" : ""}`}
															onClick={() => handlePresetSelection(key)}
															type="button"
														>
															<span className="preset-label">
																{preset.label}
															</span>
															{isSelected && (
																<span className="preset-status">✓</span>
															)}
															{isPartial && (
																<span className="preset-status">◐</span>
															)}
														</button>
													);
												})}
											</div>
											<div className="geography-global">
												<label
													className="checkbox-label global-checkbox"
													style={{ fontSize: "11px" }}
												>
													<input
														type="checkbox"
														checked={isGlobalSelected()}
														onChange={handleGlobalSelection}
													/>
													<span className="checkbox-text">
														Select All Countries ({allCountryCodes.length})
													</span>
												</label>
											</div>
											{sortedRegions.map((region) => {
												const regionCountries = countriesByRegion[region] || [];
												const isSelected = isRegionSelected(region);
												const isPartial = isRegionPartiallySelected(region);
												const isExpanded = expandedRegions[region];
												return (
													<div
														key={region}
														className="geography-region-dropdown"
													>
														<div
															className={`region-dropdown-header ${isSelected ? "selected" : ""} ${isPartial ? "partial" : ""}`}
															onClick={() => toggleRegion(region)}
														>
															<div className="region-info">
																<span className="region-name">{region}</span>
																<span className="region-count">
																	({regionCountries.length} countries)
																</span>
															</div>
															<div className="region-controls">
																<span
																	className={`region-status ${isSelected ? "selected" : ""} ${isPartial ? "partial" : ""}`}
																>
																	{isSelected ? "✓" : isPartial ? "◐" : ""}
																</span>
																<ChevronDownIcon />
															</div>
														</div>
														{isExpanded && (
															<div className="region-dropdown-content">
																<div className="region-select-all">
																	<label
																		className="checkbox-label"
																		style={{ fontSize: "10px" }}
																	>
																		<input
																			type="checkbox"
																			checked={isSelected}
																			onChange={(e) =>
																				handleRegionSelection(region, e)
																			}
																		/>
																		<span className="checkbox-text">
																			Select All {region}
																		</span>
																	</label>
																</div>
																<div className="countries-list">
																	{regionCountries.map((countryCode) => {
																		const countryInfo =
																			getCountryInfo(countryCode);
																		return (
																			<label
																				key={countryCode}
																				className="checkbox-label country-checkbox"
																				style={{ fontSize: "10px" }}
																			>
																				<input
																					type="checkbox"
																					checked={
																						filters.geography?.includes(
																							countryCode,
																						) || false
																					}
																					onChange={() =>
																						handleAppToggleArrayFilter(
																							"geography",
																							countryCode,
																						)
																					}
																				/>
																				<span
																					className="checkbox-text"
																					title={countryCode}
																				>
																					{countryInfo.name}
																				</span>
																			</label>
																		);
																	})}
																</div>
															</div>
														)}
													</div>
												);
											})}
										</>
									)}
								</div>
							</div>
						)}
					</div>

					{/* Data Period */}
					<div className="filter-section">
						<div className="section-content">
							<div
								className="filter-row"
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									padding: "8px 8px",
									marginBottom: "8px",
								}}
							>
								<span
									style={{
										fontSize: "12px",
										color: "var(--openx-text-primary)",
										fontWeight: "500",
									}}
								>
									Data Period
								</span>
								<div
									className="tab-selector"
									style={{
										display: "flex",
										backgroundColor: "var(--openx-surface)",
										border: "1px solid var(--openx-border)",
										borderRadius: "6px",
										overflow: "hidden",
									}}
								>
									<button
										type="button"
										onClick={() => handleAppUpdateFilter("period", 7)}
										style={{
											padding: "4px 12px",
											fontSize: "11px",
											border: "none",
											backgroundColor:
												filters?.period === 7
													? "var(--openx-primary)"
													: "transparent",
											color:
												filters?.period === 7
													? "white"
													: "var(--openx-text-primary)",
											cursor: "pointer",
											transition: "all 0.2s ease",
										}}
									>
										7 Days
									</button>
									<button
										type="button"
										onClick={() => handleAppUpdateFilter("period", 30)}
										style={{
											padding: "4px 12px",
											fontSize: "11px",
											border: "none",
											backgroundColor:
												filters?.period === 30 || !filters?.period
													? "var(--openx-primary)"
													: "transparent",
											color:
												filters?.period === 30 || !filters?.period
													? "white"
													: "var(--openx-text-primary)",
											cursor: "pointer",
											transition: "all 0.2s ease",
										}}
									>
										30 Days
									</button>
								</div>
							</div>
						</div>
					</div>
				</CategoryHeader>

				{/* 2. App Category */}
				<CategoryHeader
					title="Category"
					isExpanded={expandedCategories.appCategory}
					onClick={() => toggleCategory("appCategory")}
				>
					<div className="categories-grid">
						{(filterOptions?.categories || []).map((category) => (
							<span
								key={category}
								className={`category-tag ${filters.categories?.includes(category) ? "selected" : ""}`}
								onClick={() =>
									handleAppToggleArrayFilter("categories", category)
								}
							>
								{category}
							</span>
						))}
						{(!filterOptions?.categories ||
							filterOptions.categories.length === 0) && (
							<div
								style={{
									padding: "12px",
									color: "#666",
									fontSize: "11px",
									textAlign: "center",
								}}
							>
								No categories available from API
							</div>
						)}
					</div>
				</CategoryHeader>

				{/* 3. App Metadata */}
				<CategoryHeader
					title="App Metadata"
					isExpanded={expandedCategories.appMetadata}
					onClick={() => toggleCategory("appMetadata")}
				>
					{/* Developer */}
					<div className="filter-section">
						<SectionHeader
							title="Developer"
							isExpanded={expandedSections.developer}
							onToggle={() => onToggleSection("developer")}
							hasActiveFilters={filters.developers?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.developer && (
							<div className="section-content">
								<div className="search-container">
									<input
										key="developer-search"
										type="text"
										placeholder="Search developers..."
										value={developerSearch}
										onChange={(e) => setDeveloperSearch(e.target.value)}
										className="search-input"
										style={{ fontSize: "11px" }}
										autoComplete="off"
									/>
									{developerSearch && (
										<button
											onClick={() => setDeveloperSearch("")}
											className="clear-search-btn"
											type="button"
										>
											×
										</button>
									)}
								</div>

								{!developerSearch && (
									<>
										<div
											style={{
												fontSize: "10px",
												color: "#666",
												marginBottom: "8px",
											}}
										>
											Top 5 developers ({filterOptions?.developers?.length || 0}{" "}
											total available)
										</div>
										<div className="developer-tags">
											{filteredDevelopers.map((dev) => (
												<span
													key={dev}
													className={`tag tag-clickable ${filters.developers?.includes(dev) ? "tag-selected" : "tag-gray"}`}
													onClick={() =>
														handleAppToggleArrayFilter("developers", dev)
													}
													style={{ fontSize: "10px" }}
													title={dev}
												>
													{dev}
												</span>
											))}
										</div>
										{filterOptions?.developers?.length > 5 && (
											<div
												style={{
													fontSize: "10px",
													color: "#666",
													padding: "8px",
													fontStyle: "italic",
												}}
											>
												💡 Use search above to find more developers from{" "}
												{filterOptions.developers.length.toLocaleString()}{" "}
												available
											</div>
										)}
									</>
								)}

								{developerSearch && (
									<>
										<div
											style={{
												fontSize: "10px",
												color: "#666",
												marginBottom: "8px",
											}}
										>
											Found {filteredDevelopers.length} developers matching "
											{developerSearch}"
										</div>
										<div className="list-container">
											{filteredDevelopers.map((dev) => (
												<label
													key={dev}
													className="checkbox-label"
													style={{ fontSize: "11px" }}
												>
													<input
														type="checkbox"
														checked={filters.developers?.includes(dev) || false}
														onChange={() =>
															handleAppToggleArrayFilter("developers", dev)
														}
													/>
													<span className="checkbox-text">{dev}</span>
												</label>
											))}

											{filteredDevelopers.length === 0 && (
												<div
													className="no-results"
													style={{ fontSize: "11px" }}
												>
													No developers found matching "{developerSearch}"
												</div>
											)}
										</div>
									</>
								)}
							</div>
						)}
					</div>

					{/* Languages */}
					<div className="filter-section">
						<SectionHeader
							title="Languages"
							isExpanded={expandedSections.languages}
							onToggle={() => onToggleSection("languages")}
							hasActiveFilters={filters.languages?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.languages && (
							<div className="section-content">
								{availableLanguages.length > 0 ? (
									<>
										<div
											style={{
												fontSize: "10px",
												color: "#666",
												marginBottom: "8px",
											}}
										>
											{availableLanguages.length} languages available (backend
											data)
										</div>
										<div className="languages-grid">
											{availableLanguages.map((lang) => (
												<span
													key={lang.code}
													className={`category-tag ${filters.languages?.includes(lang.code) ? "selected" : ""}`}
													onClick={() =>
														handleAppToggleArrayFilter("languages", lang.code)
													}
													title={`${lang.name} (${lang.code})`}
												>
													{lang.name}
												</span>
											))}
										</div>
									</>
								) : (
									<div
										style={{
											fontSize: "11px",
											color: "#666",
											fontStyle: "italic",
											padding: "8px",
										}}
									>
										No languages available
									</div>
								)}
							</div>
						)}
					</div>

					{/* App Status */}
					<div className="filter-section">
						<SectionHeader
							title="App Status"
							isExpanded={expandedSections.appStatus}
							onToggle={() => onToggleSection("appStatus")}
							hasActiveFilters={filters.appStatus?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.appStatus && (
							<div className="section-content">
								<div className="app-status-tags">
									{appStatuses.map((status) => (
										<span
											key={status}
											className={`tag tag-clickable ${filters.appStatus?.includes(status) ? "tag-selected" : "tag-gray"}`}
											onClick={() =>
												handleAppToggleArrayFilter("appStatus", status)
											}
											style={{ fontSize: "10px" }}
										>
											{status}
										</span>
									))}
								</div>
							</div>
						)}
					</div>

					{/* App Type/Pricing */}
					<div className="filter-section">
						<SectionHeader
							title="App Type/Pricing"
							isExpanded={expandedSections.appType}
							onToggle={() => onToggleSection("appType")}
							hasActiveFilters={filters.isPaid !== null}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.appType && (
							<div className="section-content">
								<div className="toggle-group">
									<label className="toggle-label" style={{ fontSize: "11px" }}>
										App Pricing
									</label>
									<div className="toggle-buttons">
										<button
											type="button"
											className={`toggle-btn ${filters.isPaid === false ? "active" : ""}`}
											onClick={() => handleAppUpdateFilter("isPaid", false)}
											style={{ fontSize: "10px" }}
										>
											Free
										</button>
										<button
											type="button"
											className={`toggle-btn ${filters.isPaid === true ? "active" : ""}`}
											onClick={() => handleAppUpdateFilter("isPaid", true)}
											style={{ fontSize: "10px" }}
										>
											Paid
										</button>
										<button
											type="button"
											className={`toggle-btn ${filters.isPaid === null ? "active" : ""}`}
											onClick={() => handleAppUpdateFilter("isPaid", null)}
											style={{ fontSize: "10px" }}
										>
											Both
										</button>
									</div>
								</div>
							</div>
						)}
					</div>

					{/* Features */}
					<div className="filter-section">
						<SectionHeader
							title="Features"
							isExpanded={expandedSections.features}
							onToggle={() => onToggleSection("features")}
							hasActiveFilters={filters.features?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.features && (
							<div className="section-content">
								<div className="features-tags">
									{appFeatures.map((feature) => (
										<span
											key={feature}
											className={`tag tag-clickable ${filters.features?.includes(feature) ? "tag-selected" : "tag-gray"}`}
											onClick={() =>
												handleAppToggleArrayFilter("features", feature)
											}
											style={{ fontSize: "10px" }}
										>
											{feature}
										</span>
									))}
								</div>
							</div>
						)}
					</div>
				</CategoryHeader>

				{/* 4. Content & Safety */}
				<CategoryHeader
					title="Content Specifications"
					isExpanded={expandedCategories.contentSafety}
					onClick={() => toggleCategory("contentSafety")}
				>
					{/* Content Rating */}
					<div className="filter-section">
						<SectionHeader
							title="Content Rating"
							isExpanded={expandedSections.contentRating}
							onToggle={() => onToggleSection("contentRating")}
							hasActiveFilters={filters.contentRating?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.contentRating && (
							<div className="section-content">
								{/* FIXED: Show backend data indicator */}
								<div
									style={{
										fontSize: "10px",
										color: "#0076BD",
										marginBottom: "8px",
									}}
								>
									{filterOptions?.contentRatings?.length > 0
										? `${filterOptions.contentRatings.length} content ratings from backend`
										: "Using fallback content ratings"}
								</div>
								<div className="content-rating-tags">
									{contentRatings.map((rating) => (
										<span
											key={rating}
											className={`tag tag-clickable ${filters.contentRating?.includes(rating) ? "tag-selected" : "tag-gray"}`}
											onClick={() =>
												handleAppToggleArrayFilter("contentRating", rating)
											}
											style={{ fontSize: "10px" }}
										>
											{rating}
										</span>
									))}
								</div>
							</div>
						)}
					</div>

					{/* Safety Tier */}
					<div className="filter-section">
						<SectionHeader
							title="Safety Tier"
							isExpanded={expandedSections.safetyTier}
							onToggle={() => onToggleSection("safetyTier")}
							hasActiveFilters={filters.safetyTier?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.safetyTier && (
							<div className="section-content">
								<div className="safety-tier-tags">
									{(
										filterOptions?.safetyTiers || [
											"Universal",
											"Standard",
											"Limited",
											"Restricted",
										]
									).map((tier) => (
										<span
											key={tier}
											className={`tag tag-clickable ${filters.safetyTier?.includes(tier) ? "tag-selected" : "tag-gray"}`}
											onClick={() =>
												handleAppToggleArrayFilter("safetyTier", tier)
											}
											style={{ fontSize: "10px" }}
										>
											{tier}
										</span>
									))}
								</div>
							</div>
						)}
					</div>

					{/* COPPA Compliance */}
					<div className="filter-section">
						<SectionHeader
							title="COPPA Compliant"
							isExpanded={expandedSections.coppa}
							onToggle={() => onToggleSection("coppa")}
							hasActiveFilters={filters.coppa !== null}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.coppa && (
							<div className="section-content">
								<div className="toggle-group">
									<div className="toggle-buttons">
										<button
											type="button"
											className={`toggle-btn ${filters.coppa === true ? "active" : ""}`}
											onClick={() => handleAppUpdateFilter("coppa", true)}
											style={{ fontSize: "10px" }}
										>
											Yes
										</button>
										<button
											type="button"
											className={`toggle-btn ${filters.coppa === false ? "active" : ""}`}
											onClick={() => handleAppUpdateFilter("coppa", false)}
											style={{ fontSize: "10px" }}
										>
											No
										</button>
										<button
											type="button"
											className={`toggle-btn ${filters.coppa === null ? "active" : ""}`}
											onClick={() => handleAppUpdateFilter("coppa", null)}
											style={{ fontSize: "10px" }}
										>
											Both
										</button>
									</div>
								</div>
							</div>
						)}
					</div>
				</CategoryHeader>

				{/* 5. App Popularity */}
				<CategoryHeader
					title="App Popularity"
					isExpanded={expandedCategories.appPopularity}
					onClick={() => toggleCategory("appPopularity")}
				>
					{/* Ratings & Reviews */}
					<div className="filter-section">
						<SectionHeader
							title="Ratings & Reviews"
							isExpanded={expandedSections.appRating}
							onToggle={() => onToggleSection("appRating")}
							hasActiveFilters={
								filters.minRating > 1 || filters.minRatingCount > 0
							}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.appRating && (
							<div className="section-content">
								<div className="slider-container">
									{/* FIXED: Simplified Rating Slider */}
									<DraggableSlider
										label="Min. Rating"
										value={filters?.minRating ?? 1}
										min={0}
										max={5}
										step={0.1}
										onChange={(value) => onUpdateFilter("minRating", value)} // Use direct prop
										formatValue={(value) => `${value.toFixed(1)} ⭐`}
										availableValues={filterOptions?.rawRatings || []}
										snapToAvailable={false} // Set to false since hasBackendRatings is undefined
									/>

									{/* FIXED: Simplified Rating Count Slider */}
									<DraggableSlider
										label="Min. Reviews"
										value={filters?.minRatingCount ?? 0}
										min={0}
										max={1000000} // Fixed max value instead of complex calculation
										step={1000}
										onChange={(value) =>
											onUpdateFilter("minRatingCount", value)
										} // Use direct prop
										formatValue={(value) => value.toLocaleString()}
										availableValues={filterOptions?.rawRatingCounts || []}
										snapToAvailable={false} // Set to false since hasBackendRatingCounts is undefined
									/>
								</div>

								{/* FIXED: Show backend data info */}
								{(hasBackendRatings || hasBackendRatingCounts) && (
									<div
										style={{
											fontSize: "9px",
											color: "#0076BD",
											fontStyle: "italic",
											marginTop: "8px",
											padding: "4px",
											backgroundColor: "#f0f9ff",
											borderRadius: "3px",
										}}
									>
										Using real backend data:{" "}
										{backendDataStats?.ratingsCount || 0} rating values,{" "}
										{backendDataStats?.ratingCountsCount || 0} review counts
									</div>
								)}
							</div>
						)}
					</div>

					{/* Download Ranges */}
					<div className="filter-section">
						<SectionHeader
							title="Download Ranges"
							isExpanded={expandedSections.downloads}
							onToggle={() => onToggleSection("downloads")}
							hasActiveFilters={filters.downloadRanges?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.downloads && (
							<div className="section-content">
								{/* FIXED: Show backend data indicator */}
								<div
									style={{
										fontSize: "10px",
										color: "#0076BD",
										marginBottom: "8px",
									}}
								>
									{filterOptions?.downloadRanges?.length > 0
										? `${filterOptions.downloadRanges.length} download ranges from backend (${backendDataStats?.downloadsCount || 0} raw values)`
										: "Using fallback download ranges"}
								</div>
								<div className="download-ranges-tags">
									{(
										filterOptions?.downloadRanges || [
											"1-100",
											"100-1K",
											"1K-10K",
											"10K-50K",
											"50K-100K",
											"100K-1M",
											"1M-10M",
											"10M-50M",
											"50M-100M",
											"100M+",
										]
									).map((range) => (
										<span
											key={range}
											className={`tag tag-clickable ${filters.downloadRanges?.includes(range) ? "tag-selected" : "tag-gray"}`}
											onClick={() =>
												handleAppToggleArrayFilter("downloadRanges", range)
											}
											style={{ fontSize: "10px" }}
										>
											{range}
										</span>
									))}
								</div>
							</div>
						)}
					</div>

					{/* App Age */}
					<div className="filter-section">
						<SectionHeader
							title="App Age"
							isExpanded={expandedSections.appAge}
							onToggle={() => onToggleSection("appAge")}
							hasActiveFilters={filters.appAge?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.appAge && (
							<div className="section-content">
								<div className="app-age-tags">
									{[
										"New (0-1 year)",
										"Established (1-3 years)",
										"Mature (3+ years)",
									].map((age) => (
										<span
											key={age}
											className={`tag tag-clickable ${filters.appAge?.includes(age) ? "tag-selected" : "tag-gray"}`}
											onClick={() => handleAppToggleArrayFilter("appAge", age)}
											style={{ fontSize: "10px" }}
										>
											{age}
										</span>
									))}
								</div>
							</div>
						)}
					</div>

					{/* Last Updated */}
					<div className="filter-section">
						<SectionHeader
							title="Last Updated"
							isExpanded={expandedSections.lastUpdated}
							onToggle={() => onToggleSection("lastUpdated")}
							hasActiveFilters={filters.lastUpdated?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.lastUpdated && (
							<div className="section-content">
								<div className="last-updated-tags">
									{[
										"Last 30 days",
										"Last 3 months",
										"Last 6 months",
										"Last year",
										"Over 1 year ago",
									].map((period) => (
										<span
											key={period}
											className={`tag tag-clickable ${filters.lastUpdated?.includes(period) ? "tag-selected" : "tag-gray"}`}
											onClick={() =>
												handleAppToggleArrayFilter("lastUpdated", period)
											}
											style={{ fontSize: "10px" }}
										>
											{period}
										</span>
									))}
								</div>
							</div>
						)}
					</div>
				</CategoryHeader>

				{/* 6. Performance Metrics */}
				<CategoryHeader
					title="Performance Metrics"
					isExpanded={expandedCategories.performance}
					onClick={() => toggleCategory("performance")}
				>
					{expandedCategories.performance && (
						<div className="category-content">
							<div className="slider-group">
								{/* Viewability Slider */}
								<DraggableSlider
									label="Min. Viewability"
									value={filters?.minViewability ?? 80}
									min={0}
									max={100}
									step={1}
									unit="%"
									onChange={(value) =>
										handleAppUpdateFilter("minViewability", value)
									}
								/>

								{/*Min CTR/VTR Slider*/}
								{/* <DraggableSlider
                                    label="Min. CTR/VTR"
                                    value={filters?.minCTR ?? 1.0}
                                    min={0}
                                    max={10}
                                    step={0.1}
                                    unit="%"
                                    onChange={(value) => handleAppUpdateFilter('minCTR', value)}
                                    formatValue={(value) => `${value.toFixed(1)}%`}
                                /> */}

								{/* Custom Performance Filters */}
								<div
									style={{
										borderTop: "1px solid #e0e0e0",
										paddingTop: "16px",
										marginTop: "16px",
									}}
								>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											marginBottom: "12px",
										}}
									>
										<label
											style={{ fontSize: "11px", fontWeight: "600", margin: 0 }}
										>
											Custom Performance Filter
										</label>
										<button
											onClick={addCustomFilter}
											style={{
												background: "none",
												border: "1px solid #0076BD",
												borderRadius: "4px",
												color: "#0076BD",
												cursor: "pointer",
												padding: "4px 8px",
												fontSize: "10px",
												display: "flex",
												alignItems: "center",
												gap: "4px",
											}}
											title="Add Custom Filter"
										>
											<span style={{ fontSize: "12px", fontWeight: "bold" }}>
												+
											</span>
											Add Filter
										</button>
									</div>

									{/* FIXED: Show backend columns indicator */}
									<div
										style={{
											fontSize: "9px",
											color: "#0076BD",
											marginBottom: "8px",
										}}
									>
										{availableColumns.length} performance columns available
										{filterOptions?.availableColumns?.length > 0
											? " (from backend)"
											: " (fallback)"}
									</div>

									<div className="custom-filters-list">
										{(ensuredFilters.customFilters || []).map(
											(customFilter) => {
												const filterToRemove =
													ensuredFilters.customFilters.find(
														(filter) => filter.id === customFilter.id,
													);
												const isDefaultFilter =
													filterToRemove &&
													(filterToRemove.column === "cprmarketrequests" ||
														filterToRemove.column === "impressions");
												return (
													<div
														key={customFilter.id}
														style={{
															display: "flex",
															alignItems: "flex-end",
															gap: "6px",
															marginBottom: "8px",
															padding: "6px",
															border: "1px solid #e0e0e0",
															borderRadius: "4px",
															backgroundColor: "#f9f9f9",
														}}
													>
														{/* Column selector */}
														<div style={{ flex: "1" }}>
															<label
																style={{
																	fontSize: "9px",
																	marginBottom: "2px",
																	display: "block",
																	color: "#666",
																}}
															>
																Column:
															</label>
															<select
																value={customFilter.column}
																onChange={(e) =>
																	updateCustomFilter(
																		customFilter.id,
																		"column",
																		e.target.value,
																	)
																}
																style={{
																	width: "100%",
																	padding: "4px 6px",
																	fontSize: "10px",
																	border: "1px solid #ddd",
																	borderRadius: "3px",
																	backgroundColor: "white",
																}}
															>
																{availableColumns.map((column) => (
																	<option
																		key={column.value}
																		value={column.value}
																	>
																		{column.label}
																	</option>
																))}
															</select>
														</div>
														{/* Threshold input */}
														<div style={{ flex: "1" }}>
															<label
																style={{
																	fontSize: "9px",
																	marginBottom: "2px",
																	display: "block",
																	color: "#666",
																}}
															>
																Min. Value:
															</label>
															<CustomFilterInput
																customFilter={customFilter}
																onUpdate={updateCustomFilter}
															/>
														</div>
														{/* Remove button */}
														<button
															onClick={() =>
																removeCustomFilter(customFilter.id)
															}
															style={{
																background: "none",
																border: "1px solid #dc3545",
																borderRadius: "3px",
																color: "#dc3545",
																cursor: "pointer",
																padding: "4px 6px",
																fontSize: "10px",
																width: "24px",
																height: "26px",
															}}
															title="Remove Filter"
														>
															×
														</button>
													</div>
												);
											},
										)}
									</div>
									{(!filters.customFilters ||
										filters.customFilters.length === 0) && (
										<div
											style={{
												fontSize: "10px",
												color: "#999",
												textAlign: "center",
												padding: "8px",
												fontStyle: "italic",
											}}
										>
											Click "Add Filter" to create custom performance filters
										</div>
									)}
								</div>
							</div>
						</div>
					)}
				</CategoryHeader>

				{/* 7. Publishers Category */}
				<CategoryHeader
					title="Publishers"
					isExpanded={expandedCategories.publishers}
					onClick={() => toggleCategory("publishers")}
				>
					{expandedCategories.publishers && (
						<div className="section-content" style={{ padding: "12px 0" }}>
							<div
								className="filter-row"
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									padding: "8px 8px",
									marginBottom: "12px",
								}}
							>
								<span
									style={{
										fontSize: "12px",
										color: "var(--openx-text-primary)",
										fontWeight: "500",
									}}
								>
									Is Direct
								</span>
								<div
									className="tab-selector"
									style={{
										display: "flex",
										backgroundColor: "var(--openx-surface)",
										border: "1px solid var(--openx-border)",
										borderRadius: "6px",
										overflow: "hidden",
									}}
								>
									<button
										type="button"
										onClick={() => handleAppUpdateFilter("isDirect", true)}
										style={{
											padding: "4px 12px",
											fontSize: "11px",
											border: "none",
											backgroundColor:
												filters?.isDirect === true
													? "var(--openx-primary)"
													: "transparent",
											color:
												filters?.isDirect === true
													? "white"
													: "var(--openx-text-primary)",
											cursor: "pointer",
											transition: "all 0.2s ease",
										}}
									>
										Yes
									</button>
									<button
										type="button"
										onClick={() => handleAppUpdateFilter("isDirect", false)}
										style={{
											padding: "4px 12px",
											fontSize: "11px",
											border: "none",
											backgroundColor:
												filters?.isDirect === false
													? "var(--openx-primary)"
													: "transparent",
											color:
												filters?.isDirect === false
													? "white"
													: "var(--openx-text-primary)",
											cursor: "pointer",
											transition: "all 0.2s ease",
										}}
									>
										No
									</button>
									<button
										type="button"
										onClick={() => handleAppUpdateFilter("isDirect", null)}
										style={{
											padding: "4px 12px",
											fontSize: "11px",
											border: "none",
											backgroundColor:
												filters?.isDirect === null ||
												filters?.isDirect === undefined
													? "var(--openx-primary)"
													: "transparent",
											color:
												filters?.isDirect === null ||
												filters?.isDirect === undefined
													? "white"
													: "var(--openx-text-primary)",
											cursor: "pointer",
											transition: "all 0.2s ease",
										}}
									>
										Both
									</button>
								</div>
							</div>

							<div className="publisher-selection">
								<div className="publisher-search">
									<PublisherSearchInput
										value={publisherSearchInput}
										onChange={handlePublisherSearchChange}
									/>
								</div>

								<div
									className="publisher-list"
									style={{ maxHeight: "150px", overflowY: "auto" }}
								>
									{filteredPublishers.length > 0 ? (
										<>
											<div
												style={{
													fontSize: "10px",
													color: "#666",
													marginBottom: "8px",
												}}
											>
												{debouncedPublisherSearch
													? `Found ${filteredPublishers.length} publishers matching "${debouncedPublisherSearch}"`
													: `${filteredPublishers.length} publishers available ${filterOptions?.publishers?.length > 50 ? "(showing first 50)" : ""}`}
												{filterOptions?.publishers?.length > 0 &&
													" - backend data"}
											</div>
											{filteredPublishers.map((pub) => (
												<label
													key={pub}
													className="checkbox-label"
													style={{ fontSize: "11px" }}
												>
													<input
														type="checkbox"
														checked={(filters?.publishers || []).includes(pub)}
														onChange={() =>
															handleAppToggleArrayFilter("publishers", pub)
														}
													/>
													<span className="checkbox-text">{pub}</span>
												</label>
											))}
										</>
									) : (
										<div
											style={{
												padding: "12px",
												color: "#666",
												fontSize: "11px",
												textAlign: "center",
												fontStyle: "italic",
											}}
										>
											{debouncedPublisherSearch
												? `No publishers found matching "${debouncedPublisherSearch}"`
												: "No publishers available"}
										</div>
									)}
								</div>
							</div>
						</div>
					)}
				</CategoryHeader>

				{/* Limit Input and Upload Section */}
				<div
					style={{
						marginTop: "16px",
						padding: "12px",
						borderTop: "1px solid #e5e7eb",
						backgroundColor: "#f9fafb",
					}}
				>
					{/* Limit Input Field */}
					<div style={{ marginBottom: "12px" }}>
						<label
							style={{
								display: "block",
								fontSize: "11px",
								fontWeight: "500",
								color: "#374151",
								marginBottom: "4px",
							}}
						>
							Result Limit:
						</label>
						<LimitInput
							value={filters?.limit || 1000}
							onChange={(value) => handleAppUpdateFilter("limit", value)}
						/>
					</div>

					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							gap: "4px",
							margin: "0 0 8px 0",
							position: "relative",
						}}
					>
						<p
							style={{
								margin: "0",
								fontSize: "11px",
								color: "#6b7280",
								textAlign: "center",
							}}
						>
							Upload CSV files for bundle id filtering
						</p>
						<div
							style={{ position: "relative", display: "inline-flex" }}
							onMouseEnter={() => setShowUploadTooltip(true)}
							onMouseLeave={() => setShowUploadTooltip(false)}
						>
							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									justifyContent: "center",
									width: "14px",
									height: "14px",
									borderRadius: "50%",
									backgroundColor: "#6b7280",
									color: "white",
									fontSize: "9px",
									fontWeight: "bold",
									cursor: "pointer",
								}}
							>
								i
							</div>

							{showUploadTooltip && (
								<div
									style={{
										position: "absolute",
										top: "-45px",
										left: "-50px",
										transform: "translateX(-50%)",
										background: "#333",
										color: "white",
										padding: "6px 8px",
										borderRadius: "4px",
										fontSize: "10px",
										width: "120px",
										textAlign: "center",
										lineHeight: "1.3",
										zIndex: 1000,
									}}
								>
									CSV file must contain a bundle id column
								</div>
							)}
						</div>
					</div>

					<div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
						{/* Include List Button */}
						<div style={{ flex: 1 }}>
							<input
								type="file"
								accept=".csv"
								onChange={(e) =>
									handleFileUpload(e.target.files[0], "includeList")
								}
								style={{ display: "none" }}
								id="include-upload"
								disabled={uploadStates.includeList.isUploading}
							/>
							<label
								htmlFor="include-upload"
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: "4px",
									width: "100%",
									padding: "6px 8px",
									background: uploadStates.includeList.isUploading
										? "#6b7280"
										: "#0076BD",
									color: "white",
									border: "none",
									borderRadius: "4px",
									fontSize: "10px",
									fontWeight: "500",
									cursor: uploadStates.includeList.isUploading
										? "not-allowed"
										: "pointer",
									transition: "all 0.2s",
									textDecoration: "none",
									boxSizing: "border-box",
								}}
							>
								{uploadStates.includeList.isUploading ? (
									<>⏳ Uploading...</>
								) : (
									<>
										<UploadIcon />
										Allow List
									</>
								)}
							</label>
						</div>

						{/* Exclude List Button */}
						<div style={{ flex: 1 }}>
							<input
								type="file"
								accept=".csv"
								onChange={(e) =>
									handleFileUpload(e.target.files[0], "blockedList")
								}
								style={{ display: "none" }}
								id="exclude-upload"
								disabled={uploadStates.blockedList.isUploading}
							/>
							<label
								htmlFor="exclude-upload"
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: "4px",
									width: "100%",
									padding: "6px 8px",
									background: uploadStates.blockedList.isUploading
										? "#6b7280"
										: "#dc2626",
									color: "white",
									border: "none",
									borderRadius: "4px",
									fontSize: "10px",
									fontWeight: "500",
									cursor: uploadStates.blockedList.isUploading
										? "not-allowed"
										: "pointer",
									transition: "all 0.2s",
									textDecoration: "none",
									boxSizing: "border-box",
								}}
							>
								{uploadStates.blockedList.isUploading ? (
									<>⏳ Uploading...</>
								) : (
									<>
										<UploadIcon />
										Block List
									</>
								)}
							</label>
						</div>
					</div>

					{/* Display uploaded file names with remove buttons */}
					{uploadStates.includeList.isUploaded && (
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								padding: "4px 6px",
								backgroundColor: "#dcfce7",
								border: "1px solid #22c55e",
								borderRadius: "3px",
								fontSize: "9px",
								marginBottom: "4px",
							}}
						>
							<span style={{ color: "#15803d", fontWeight: "500" }}>
								✓ Allow List: {uploadStates.includeList.fileName} (
								{uploadStates.includeList.bundleIds.length} bundle IDs)
							</span>
							<button
								onClick={() => clearUploadedList("includeList")}
								style={{
									background: "none",
									border: "none",
									color: "#dc2626",
									cursor: "pointer",
									padding: "2px",
									borderRadius: "2px",
									display: "flex",
									alignItems: "center",
									fontSize: "12px",
								}}
								title="Remove Allow List"
							>
								<RemoveIcon />
							</button>
						</div>
					)}

					{uploadStates.blockedList.isUploaded && (
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								padding: "4px 6px",
								backgroundColor: "#fee2e2",
								border: "1px solid #dc2626",
								borderRadius: "3px",
								fontSize: "9px",
								marginBottom: "4px",
							}}
						>
							<span style={{ color: "#dc2626", fontWeight: "500" }}>
								✗ Block List: {uploadStates.blockedList.fileName} (
								{uploadStates.blockedList.bundleIds.length} bundle IDs)
							</span>
							<button
								onClick={() => clearUploadedList("blockedList")}
								style={{
									background: "none",
									border: "none",
									color: "#dc2626",
									cursor: "pointer",
									padding: "2px",
									borderRadius: "2px",
									display: "flex",
									alignItems: "center",
									fontSize: "12px",
								}}
								title="Remove Block List"
							>
								<RemoveIcon />
							</button>
						</div>
					)}

					{/* Error Messages */}
					{(uploadStates.includeList.error ||
						uploadStates.blockedList.error) && (
						<div
							style={{
								fontSize: "9px",
								color: "#dc2626",
								textAlign: "center",
								backgroundColor: "#fef2f2",
								padding: "4px 6px",
								borderRadius: "3px",
								border: "1px solid #fecaca",
							}}
						>
							{uploadStates.includeList.error || uploadStates.blockedList.error}
						</div>
					)}
				</div>
			</div>

			{/* All the existing styles remain the same */}
			<style jsx>{`
                .filters-content {
                    scroll-behavior: auto !important;
                    overflow-x: hidden;
                    overflow-y: auto;
                    height: calc(100vh - 120px);
                    position: relative;
                }

                .filter-section,
                .category-group,
                .region-group {
                    contain: layout style;
                }

                .section-content,
                .category-content,
                .category-group-content,
                .region-dropdown-content {
                    min-height: 0;
                }

                .enhanced-slider-container {
                    margin-bottom: 5px;
                    user-select: none;
                    -webkit-user-select: none;
                    -moz-user-select: none;
                    -ms-user-select: none;
                    padding: 0 12px;
                    will-change: transform;
                }

                .enhanced-slider-label {
                    display: block;
                    margin-bottom: 12px;
                    font-size: 11px;
                    font-weight: 600;
                    color: #374151;
                    user-select: none;
                    pointer-events: none;
                }

                .enhanced-slider-wrapper {
                    position: relative;
                    padding: 12px 0;
                    will-change: transform;
                }

                .enhanced-slider-track {
                    position: relative;
                    width: 100%;
                    height: 3px;
                    background: #e5e7eb;
                    border-radius: 4px;
                    cursor: pointer;
                    transition: all 0.15s ease;
                    outline: none;
                    overflow: visible;
                    padding: 8px 0;
                    margin: -8px 0;
                    -webkit-touch-callout: none;
                    -webkit-user-select: none;
                    -khtml-user-select: none;
                    -moz-user-select: none;
                    -ms-user-select: none;
                    user-select: none;
                    -webkit-tap-highlight-color: transparent;
                    touch-action: none;
                    will-change: transform, background-color;
                }

                .enhanced-slider-track:hover {
                    background: #d1d5db;
                    box-shadow: 0 2px 4px rgba(0, 118, 189, 0.2);
                }

                .enhanced-slider-track:focus {
                    box-shadow: 0 0 0 3px rgba(0, 118, 189, 0.3);
                    outline: none;
                }

                .enhanced-slider-track:focus-visible {
                    box-shadow: 0 0 0 3px rgba(0, 118, 189, 0.4);
                    outline: 2px solid transparent;
                }

                .enhanced-slider-track.dragging {
                    cursor: grabbing !important;
                    box-shadow: 0 4px 8px rgba(0, 118, 189, 0.3);
                    user-select: none !important;
                    -webkit-user-select: none !important;
                    background: #d1d5db;
                }

                .enhanced-slider-fill {
                    position: absolute;
                    left: 0;
                    top: 8px;
                    height: 6px;
                    background: linear-gradient(135deg, #0076BD 0%, #0056b3 100%);
                    border-radius: 4px;
                    transition: none;
                    pointer-events: none;
                    user-select: none;
                    max-width: 100%;
                    will-change: width;
                }

                .enhanced-slider-thumb {
                    position: absolute;
                    top: 50%;
                    width: 18px;
                    height: 18px;
                    background: #0076BD;
                    border: 3px solid white;
                    border-radius: 50%;
                    cursor: grab;
                    transform: translate(-50%, -50%);
                    transition: box-shadow 0.15s ease, background-color 0.15s ease;
                    box-shadow: 0 2px 6px rgba(0, 118, 189, 0.4);
                    z-index: 10;
                    user-select: none;
                    pointer-events: auto;
                    will-change: transform, background-color;
                }

                .enhanced-slider-thumb::before {
                    content: '';
                    position: absolute;
                    top: -15px;
                    left: -15px;
                    right: -15px;
                    bottom: -15px;
                    border-radius: 50%;
                    background: transparent;
                    cursor: grab;
                }

                .enhanced-slider-thumb:hover {
                    background: #0056b3;
                    transform: translate(-50%, -50%) scale(1.1);
                    box-shadow: 0 3px 8px rgba(0, 118, 189, 0.5);
                    transition: all 0.15s ease;
                }

                .enhanced-slider-thumb.dragging {
                    cursor: grabbing !important;
                    background: #003d82;
                    transform: translate(-50%, -50%) scale(1.15);
                    box-shadow: 0 4px 12px rgba(0, 118, 189, 0.6);
                    transition: none !important;
                }

                .enhanced-slider-thumb.dragging::before {
                    cursor: grabbing !important;
                }

                .enhanced-slider-labels {
                    display: flex;
                    justify-content: space-between;
                    margin-top: 8px;
                    font-size: 10px;
                    color: #6b7280;
                    font-weight: 500;
                    user-select: none;
                    pointer-events: none;
                }

                .slider-group {
                    -webkit-user-select: none;
                    -moz-user-select: none;
                    -ms-user-select: none;
                    user-select: none;
                    will-change: transform;
                }

                .enhanced-slider-track:not(.dragging):hover .enhanced-slider-thumb {
                    background: #0056b3;
                    transform: translate(-50%, -50%) scale(1.05);
                }

                .enhanced-slider-track:not(.dragging):active .enhanced-slider-thumb {
                    transform: translate(-50%, -50%) scale(1.1);
                }

                .enhanced-slider-container input[type="range"] {
                    display: none !important;
                }

                .enhanced-slider-track *,
                .enhanced-slider-thumb,
                .enhanced-slider-fill {
                    -webkit-user-drag: none;
                    -khtml-user-drag: none;
                    -moz-user-drag: none;
                    -o-user-drag: none;
                    user-drag: none;
                }

                @media (max-width: 768px) {
                    .enhanced-slider-thumb {
                        width: 22px;
                        height: 22px;
                        border-width: 3px;
                    }

                    .enhanced-slider-thumb::before {
                        top: -18px;
                        left: -18px;
                        right: -18px;
                        bottom: -18px;
                    }

                    .enhanced-slider-track {
                        height: 8px;
                        padding: 12px 0;
                        margin: -12px 0;
                    }

                    .enhanced-slider-fill {
                        top: 12px;
                        height: 8px;
                    }

                    .enhanced-slider-wrapper {
                        padding: 15px 0;
                    }
                }

                .enhanced-slider-track.dragging .enhanced-slider-fill {
                    pointer-events: none;
                }

                .enhanced-slider-track.dragging .enhanced-slider-fill {
                    background: linear-gradient(135deg, #003d82 0%, #002a5c 100%);
                }

                .presets-buttons {
                    margin-bottom: 12px;
                    display: flex;
                    gap: 6px;
                    flex-wrap: wrap;
                }
                .preset-btn {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    padding: 4px 8px;
                    border: 1px solid #e5e7eb;
                    border-radius: 4px;
                    background: white;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-size: 10px;
                    font-weight: 500;
                    color: #374151;
                    white-space: nowrap;
                }
                .preset-btn.selected {
                    background: #0076BD;
                    border-color: #0076BD;
                    color: white;
                }
                .preset-btn.partial {
                    background: rgba(0, 118, 189, 0.1);
                    border-color: #0076BD;
                    color: #0076BD;
                }
                .preset-flag {
                    font-size: 12px;
                }
                .preset-label {
                    font-size: 10px;
                }
                .preset-status {
                    font-size: 10px;
                    font-weight: bold;
                }
                .category-search-container {
                    position: relative;
                    margin-bottom: 12px;
                }
                .category-search-input {
                    width: 100%;
                    padding: 6px 25px 6px 8px;
                    border: 1px solid #d1d5db;
                    border-radius: 4px;
                    font-size: 11px;
                    background-color: white;
                    transition: all 0.2s;
                }
                .category-search-input:focus {
                    outline: none;
                    border-color: #0076BD;
                    box-shadow: 0 0 0 2px rgba(0, 118, 189, 0.1);
                }
                .close-btn {
                    background: none;
                    border: none;
                    color: var(--openx-text-secondary);
                    cursor: pointer;
                    padding: 2px;
                    border-radius: 2px;
                    transition: all 0.2s;
                }
                .section-header {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 6px;
                    background: #ffffff;
                    border: 1px solid #e9ecef;
                    border-radius: 4px;
                    cursor: pointer;
                    transition: all 0.2s;
                    margin-bottom: 2px;
                    font-size: 11px;
                    font-weight: 500;
                }
                .section-title {
                    flex: 1;
                    font-size: 11px;
                    font-weight: 500;
                    color: inherit;
                }
                .section-indicators {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                }
                .section-content {
                    padding-left: 20px;
                }
                .checkbox-label {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 4px 6px;
                    font-size: 10px;
                    font-weight: 400;
                    margin-bottom: 1px;
                    cursor: pointer;
                    border-radius: 3px;
                    transition: all 0.2s;
                }
                .checkbox-text {
                    color: inherit;
                    font-size: 10px;
                }
                .search-container {
                    position: relative;
                    margin-bottom: 12px;
                }
                .search-input {
                    width: 100%;
                    padding: 6px 25px 6px 8px;
                    border: 1px solid #d1d5db;
                    border-radius: 4px;
                    font-size: 11px;
                    background-color: white;
                    transition: all 0.2s;
                }
                .search-input:focus {
                    outline: none;
                    border-color: #0076BD;
                    box-shadow: 0 0 0 2px rgba(0, 118, 189, 0.1);
                }
                .clear-search-btn {
                    position: absolute;
                    right: 6px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: none;
                    border: none;
                    color: #6b7280;
                    cursor: pointer;
                    font-size: 16px;
                    padding: 2px;
                    border-radius: 2px;
                }
                .list-container {
                    max-height: 200px;
                    overflow-y: auto;
                }
                .no-results {
                    padding: 8px;
                    color: #6b7280;
                    text-align: center;
                    font-style: italic;
                    font-size: 11px;
                }
                .categories-grid,
                .languages-grid,
                .features-grid,
                .media-subtype-tags,
                .device-type-tags,
                .video-placement-tags,
                .safety-tier-tags,
                .app-store-tags,
                .platform-tags,
                .app-status-tags,
                .content-rating-tags,
                .features-tags,
                .download-ranges-tags,
                .app-age-tags,
                .last-updated-tags,
                .market-source-tags,
                .developer-tags {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 4px;
                }
                .category-tag,
                .language-tag,
                .feature-tag,
                .tag {
                    font-size: 9px;
                    padding: 2px 6px;
                    border-radius: 12px;
                    background: #f3f4f6;
                    color: black;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s;
                    user-select: none;
                }
                .tag-clickable {
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .tag-gray {
                    background: #f3f4f6;
                    color: #374151;
                    border: 1px solid #e5e7eb;
                }
                .tag-selected {
                    background: #0076BD;
                    color: white;
                    border: 1px solid #0076BD;
                }
                .category-tag.selected,
                .language-tag.selected,
                .feature-tag.selected {
                    background: #0076BD;
                    color: white;
                }
                .header-toggle-container {
                    margin-left: 12px;
                    display: inline-flex;
                    align-items: center;
                }
                .header-toggle-label {
                    position: relative;
                    display: inline-block;
                    width: 34px;
                    height: 18px;
                    cursor: pointer;
                }
                .header-toggle-input {
                    opacity: 0;
                    width: 0;
                    height: 0;
                }
                .header-toggle-slider {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: #ccc;
                    border-radius: 18px;
                    transition: .4s;
                }
                .header-toggle-slider:before {
                    position: absolute;
                    content: "";
                    height: 14px;
                    width: 14px;
                    left: 2px;
                    bottom: 2px;
                    background-color: white;
                    border-radius: 50%;
                    transition: .4s;
                }
                .header-toggle-input:checked + .header-toggle-slider {
                    background-color: #0076BD;
                }
                .header-toggle-input:checked + .header-toggle-slider:before {
                    transform: translateX(16px);
                }
                .slider-container {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }
                .slider-label {
                    font-size: 11px;
                    font-weight: 500;
                    color: var(--openx-text-primary);
                }
                .slider {
                    width: 100%;
                    height: 4px;
                    border-radius: 2px;
                    background: var(--openx-border);
                    outline: none;
                    cursor: pointer;
                }
                .slider.slider-blue::-webkit-slider-thumb {
                    appearance: none;
                    width: 16px;
                    height: 16px;
                    border-radius: 50%;
                    background: #0076BD;
                    cursor: pointer;
                    border: 2px solid white;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                }
                .toggle-group {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }
                .toggle-label {
                    font-size: 11px;
                    font-weight: 500;
                    color: var(--openx-text-primary);
                }
                .toggle-buttons {
                    display: flex;
                    border: 1px solid var(--openx-border);
                    border-radius: 6px;
                    overflow: hidden;
                }
                .toggle-btn {
                    flex: 1;
                    padding: 4px 8px;
                    border: none;
                    background: var(--openx-surface);
                    color: var(--openx-text-secondary);
                    cursor: pointer;
                    transition: all 0.2s;
                    font-size: 10px;
                }
                .toggle-btn.active {
                    background: #0076BD;
                    color: white;
                }
                .geography-section {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                .geography-search {
                    margin-bottom: 12px;
                }
                .geography-search-results {
                    margin-bottom: 16px;
                    border: 1px solid #e5e7eb;
                    border-radius: 6px;
                    background: white;
                    overflow: hidden;
                }
                .search-results-header {
                    padding: 8px 12px;
                    background: #f8f9fa;
                    border-bottom: 1px solid #e5e7eb;
                }
                .search-results-list {
                    max-height: 300px;
                    overflow-y: auto;
                    padding: 8px;
                }
                .country-search-result {
                    padding: 4px 8px;
                    border-radius: 3px;
                    margin-bottom: 2px;
                }
                .country-code-hint {
                    color: #6b7280;
                    font-size: 9px;
                    margin-left: 4px;
                }
                .geography-global {
                    margin-bottom: 12px;
                    padding: 8px;
                    background: #f0f9ff;
                    border: 1px solid #0076BD;
                    border-radius: 6px;
                }
                .global-checkbox {
                    font-weight: 600;
                    color: #0076BD;
                }
                .geography-region-dropdown {
                    border: 1px solid #e5e7eb;
                    border-radius: 6px;
                    background: white;
                    overflow: hidden;
                }
                .region-dropdown-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 8px 12px;
                    cursor: pointer;
                    transition: all 0.2s;
                    background: #f8f9fa;
                }
                .region-dropdown-header.selected {
                    background: #0076BD;
                    color: white;
                }
                .region-dropdown-header.partial {
                    background: rgba(0, 118, 189, 0.1);
                    border-left: 3px solid #0076BD;
                }
                .region-info {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }
                .region-name {
                    font-size: 11px;
                    font-weight: 600;
                    color: inherit;
                }
                .region-count {
                    font-size: 9px;
                    color: inherit;
                    opacity: 0.7;
                }
                .region-controls {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .region-status {
                    font-size: 12px;
                    font-weight: bold;
                    width: 16px;
                    text-align: center;
                }
                .region-status.selected {
                    color: white;
                }
                .region-status.partial {
                    color: #0076BD;
                }
                .region-dropdown-content {
                    border-top: 1px solid #e5e7eb;
                    padding: 8px;
                }
                .region-select-all {
                    padding: 4px 8px;
                    background: #f8f9fa;
                    border-radius: 4px;
                    margin-bottom: 8px;
                }
                .countries-list {
                    max-height: 200px;
                    overflow-y: auto;
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }
                .country-checkbox {
                    padding: 3px 8px;
                    border-radius: 3px;
                }
                .category-content {
                    border-top: 1px solid #e5e7eb;
                    padding: 8px;
                }
                .custom-filters-list {
                    max-height: 200px;
                    overflow-y: auto;
                    border: 1px solid #e5e7eb;
                    border-radius: 4px;
                    background: white;
                    padding: 4px;
                }
                .custom-filters-list:empty {
                    border: none;
                    background: transparent;
                    padding: 0;
                }
            `}</style>
		</div>
	);
};
